import React, { useState, useRef, useEffect, useCallback } from "react";
import {
  Upload,
  message,
  Modal,
  Select,
  Input,
  ConfigProvider,
  Radio,
} from "antd";
import {
  Download, Eye, Send, Plus, Trash2,
  Upload as UploadIcon, ChevronDown,
  ChevronUp, Settings2, Palette,
  Layout, Type, Image as ImageIcon,
  Save,
} from "lucide-react";
import axios from "axios";
import API_BASE from "../config/api";
import { formatItemCanvasPrice } from "../utils/menuItem";

const loadImageElement = (src) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });

/** Fetch an S3/external URL through the API proxy and return a data URL for canvas use. */
const resolveRemoteBackgroundDataUrl = async (url) => {
  const proxyUrl = `${API_BASE}/proxyImage?url=${encodeURIComponent(url)}`;
  const res = await fetch(proxyUrl);
  if (!res.ok) throw new Error("Failed to fetch background image");
  const blob = await res.blob();
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
};



const ColorField = ({ label, value, onChange, disabled, inline, compact }) => (
  <div
    style={{
      display: "flex",
      flexDirection: "column",
      gap: compact ? 4 : 6,
      minWidth: inline || compact ? 100 : undefined,
    }}
  >
    <span style={{ fontSize: compact ? 12 : 13 }}>
      {label}
      {disabled ? " (disabled)" : ""}
    </span>
    <div
      style={{ display: "flex", alignItems: "center", gap: compact ? 6 : 8 }}
    >
      <input
        type="color"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        style={{
          width: compact ? 32 : 36,
          height: compact ? 28 : 34,
          padding: 2,
          borderRadius: 6,
          border: "1px solid #d9d9d9",
          cursor: disabled ? "not-allowed" : "pointer",
          opacity: disabled ? 0.5 : 1,
        }}
      />
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        style={{
          width: inline || compact ? 68 : undefined,
          flex: inline || compact ? "none" : 1,
          padding: compact ? "4px 6px" : "6px 8px",
          borderRadius: 6,
          border: "1px solid #d9d9d9",
          fontSize: compact ? 11 : 12,
          minWidth: 0,
          opacity: disabled ? 0.5 : 1,
        }}
      />
    </div>
  </div>
);

const NumericTextInput = ({
  value,
  min = 0,
  max = 100,
  onChange,
  suffix = "%",
  hint,
  allowEmpty = false,
  placeholder,
  fallback,
}) => {
  const [isFocused, setIsFocused] = useState(false);
  const [draft, setDraft] = useState("");

  const clamp = (v) => Math.min(max, Math.max(min, v));
  const resolvedFallback = fallback ?? min;

  const displayValue = () => {
    if (isFocused) return draft;
    if (allowEmpty && (value === null || value === undefined || value === "")) {
      return "";
    }
    return String(value);
  };

  return (
    <div>
      <div className="flex items-center gap-2 w-full">
        <input
          type="text"
          inputMode="numeric"
          pattern="[0-9]*"
          placeholder={placeholder}
          value={displayValue()}
          onFocus={() => {
            setIsFocused(true);
            if (allowEmpty && (value === null || value === undefined || value === "")) {
              setDraft("");
            } else {
              setDraft(String(value));
            }
          }}
          onChange={(e) => {
            setDraft(e.target.value.replace(/\D/g, ""));
          }}
          onBlur={() => {
            if (allowEmpty && draft === "") {
              onChange(null);
            } else {
              const num = draft === "" ? resolvedFallback : parseInt(draft, 10);
              onChange(Number.isNaN(num) ? resolvedFallback : clamp(num));
            }
            setIsFocused(false);
            setDraft("");
          }}
          className="w-full min-w-0 flex-1 border border-gray-200 rounded-xl px-3 py-2.5 text-base sm:text-sm text-gray-900 outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 text-center placeholder:text-gray-400"
        />
        {suffix ? <span className="text-xs text-gray-400 shrink-0">{suffix}</span> : null}
      </div>
      {hint ? <p className="text-xs text-gray-400 mt-0.5">{hint}</p> : null}
    </div>
  );
};

// pricePositionPercent storage encoding:
//   0..100        -> direct positive offset (original range)
//   (100, 200]    -> LEGACY negative offset from an older app version:
//                    percent = raw - 200 (decodes to -100..0). Kept only so
//                    menus saved before this field existed still render
//                    correctly — nothing currently writes into this range.
//   > 1000        -> extended positive offset for 101..350: percent = raw - 1000.
//                    Deliberately far outside the legacy window above so new
//                    saves can never collide with old negative-offset data.
export const PRICE_OFFSET_MAX_PERCENT = 400;
// Combined image: how many sections can share one preview image (each gets an equal slice of the canvas width).
export const MAX_COMBINED_SECTIONS = 3;
export const decodePricePositionPercent = (raw) => {
  if (raw > 1000) return raw - 1000;
  if (raw > 100) return raw - 200;
  return raw;
};
export const encodePricePositionPercent = (percent) =>
  percent > 100 ? percent + 1000 : percent;

// Default design options - used when none saved (professional menu style)
export const DEFAULT_DESIGN_OPTIONS = {
  backgroundImageBase64: null,
  backgroundImageUrl: null,
  backgroundImageThumb: null, // Small base64 (~50KB) for preview - safe to save, no proxy needed
  savedBackgroundUrls: [], // Previously uploaded S3 URLs for this merchant
  storeName: "",
  storeAddress: "",
  storeTagline: "",
  storeHotelNameColor: "#722F37",
  storeAddressColor: "#374151",
  taglineColor: "#ffffff",
  showSectionHeader: true,
  fontSize: 30,
  fontFamily: "Arial",
  backgroundColor: "#FFFFFF",
  menuItemsColor: "#000000",
  itemNumberColor: "#000000",
  priceColor: "#000000",
  showItemNumbers: false,
  showDescription: true,
  hidePrices: false,
  itemNameBold: false,
  topMarginPercent: 20,
  pricePositionPercent: 0, // 0% starts from current 200% baseline
  itemSpacingPercent: 0, // vertical gap between item rows (0% = tightest)
  sectionNameColor: "#000000", // default section name color is black
  descriptionFontSize: null, // null = auto (fontSize * 0.55), or set 14–40
  descriptionColor: "#000000",
  descriptionWrap: true, // true = wrap to multiple lines, false = single line + ellipsis
  descriptionWidthPercent: 100, // 50, 75, 100 — width of description block within column
  descriptionMaxLines: 1, // 1–5 when wrapping
};

const resolveItemSpacingPercent = (opts) => {
  const raw =
    opts?.itemSpacingPercent !== undefined && opts?.itemSpacingPercent !== null
      ? Number(opts.itemSpacingPercent)
      : null;
  if (raw != null && Number.isFinite(raw)) {
    const scaled = opts?.itemSpacingScaleV2 ? raw : Math.max(0, raw - 1);
    return Math.min(100, Math.max(0, scaled));
  }
  const legacyPx = Number(opts?.itemSpacingPx);
  if (Number.isFinite(legacyPx) && legacyPx > 0) {
    return Math.min(100, Math.max(0, Math.min(100, legacyPx) - 1));
  }
  return 0;
};

const MenuPreviewPanel = ({
  sections,
  customerId,
  formatPrice,
  setSections,
  selectedSectionId,
  designOptions = DEFAULT_DESIGN_OPTIONS,
  onDesignOptionsChange = () => { },
  onPersistMenu = null,
  embedMode = false,
}) => {
  const [isGeneratingImage, setIsGeneratingImage] = useState(false);
  const [generatedImageUrl, setGeneratedImageUrl] = useState(null);
  const [activePreviewSection, setActivePreviewSection] = useState(
    sections[0]?.id,
  );

  /* 🔄 Regenerate states */
  const [showRegenerateModal, setShowRegenerateModal] = useState(false);
  const [regenPrompt, setRegenPrompt] = useState("");
  const [regenLoading, setRegenLoading] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [previewImageUrl, setPreviewImageUrl] = useState(null);
  const [modes, setModes] = useState([]);
  const [modesLoading, setModesLoading] = useState(false);
  const [attachLoading, setAttachLoading] = useState(false);
  const [selectedPublishModeId, setSelectedPublishModeId] = useState(null);
  const [publishAction, setPublishAction] = useState("createNew"); // 'createNew' | 'replace'
  const [promosInMode, setPromosInMode] = useState([]);
  const [promosInModeLoading, setPromosInModeLoading] = useState(false);
  const [selectedReplacePromoId, setSelectedReplacePromoId] = useState(null);
  const [showCreateModeModal, setShowCreateModeModal] = useState(false);
  const [newModeName, setNewModeName] = useState("");
  const [createModeLoading, setCreateModeLoading] = useState(false);
  const [publishedJustNow, setPublishedJustNow] = useState(false);
  const [openSections, setOpenSections] = useState({
    background: true,
    storeInfo: true,
    typography: false,
    description: false,
    layout: false,
    colors: false,
  });

  const toggleSection = (key) =>
    setOpenSections((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  const modesFetchInFlightRef = useRef(false);
  const lastModesFetchAtRef = useRef(0);

  /* Use designOptions from props (controlled by parent for persistence) */
  const sampleImageUrl =
    designOptions?.backgroundImageBase64 ||
    designOptions?.backgroundImageThumb ||
    designOptions?.backgroundImageUrl ||
    null;
  const storeName = designOptions?.storeName ?? "";
  const storeAddress = designOptions?.storeAddress ?? "";
  const storeTagline = designOptions?.storeTagline ?? "";
  const storeHotelNameColor = designOptions?.storeHotelNameColor ?? "#722F37";
  const storeAddressColor = designOptions?.storeAddressColor ?? "#374151";
  const taglineColor = designOptions?.taglineColor ?? "#ffffff";
  const rawBgColor = designOptions?.backgroundColor;
  const backgroundColor =
    !rawBgColor || rawBgColor === "#FAF6F0" || rawBgColor === "white"
      ? "#FFFFFF"
      : rawBgColor;

  const rawMenuItemsColor = designOptions?.menuItemsColor;
  const menuItemsColor =
    !rawMenuItemsColor || rawMenuItemsColor === "#1f2937"
      ? "#000000"
      : rawMenuItemsColor;

  const rawItemNumberColor = designOptions?.itemNumberColor;
  const itemNumberColor =
    !rawItemNumberColor || rawItemNumberColor === "#1f2937"
      ? "#000000"
      : rawItemNumberColor;

  const rawPriceColor = designOptions?.priceColor;
  const priceColor =
    !rawPriceColor || rawPriceColor === "#1f2937"
      ? "#000000"
      : rawPriceColor;

  const showItemNumbers = designOptions?.showItemNumbers ?? false;
  const showDescription = designOptions?.showDescription ?? true;
  const hidePrices = designOptions?.hidePrices ?? false;
  const showPrices = !hidePrices;
  const itemNameBold = designOptions?.itemNameBold ?? false;
  const showSectionHeader = designOptions?.showSectionHeader ?? true;
  const rawFontSize = Number(designOptions?.fontSize);
  const fontSize = rawFontSize >= 20 && rawFontSize <= 60 ? rawFontSize : 30;
  const fontFamily = designOptions?.fontFamily ?? "Arial";
  const topMarginPercent = designOptions?.topMarginPercent ?? 20;
  const legacyPriceOffsetPx = Math.max(
    0,
    Math.min(300, Number(designOptions?.pricePositionPx) || 0),
  );
  const rawPriceOffsetPercent =
    designOptions?.pricePositionPercent !== undefined &&
      designOptions?.pricePositionPercent !== null
      ? Number(designOptions?.pricePositionPercent)
      : null;
  const pricePositionPercent = Math.min(
    PRICE_OFFSET_MAX_PERCENT,
    Math.max(
      0,
      rawPriceOffsetPercent != null
        ? decodePricePositionPercent(rawPriceOffsetPercent)
        : Math.round((legacyPriceOffsetPx / 300) * 100),
    ),
  );
  const itemSpacingPercent = resolveItemSpacingPercent(designOptions);
  const descriptionFontSizeOption = designOptions?.descriptionFontSize;
  const rawDescColor = designOptions?.descriptionColor;
  const descriptionColorOption =
    !rawDescColor || rawDescColor === "#1f2937"
      ? "#000000"
      : rawDescColor;

  const rawSectionColor = designOptions?.sectionNameColor;
  const sectionNameColorOption =
    !rawSectionColor || rawSectionColor === "#1f2937"
      ? "#000000"
      : rawSectionColor;
  const descriptionWrap = designOptions?.descriptionWrap ?? true;
  const descriptionWidthPercent = [50, 75, 100].includes(Number(designOptions?.descriptionWidthPercent))
    ? Number(designOptions.descriptionWidthPercent)
    : 100;
  const descriptionMaxLines = Math.min(5, Math.max(1, Number(designOptions?.descriptionMaxLines) || 1));

  const updateDesignOptions = (updates) => {
    const next = { ...designOptions, ...updates };
    if (Object.prototype.hasOwnProperty.call(updates, "itemSpacingPercent")) {
      next.itemSpacingScaleV2 = true;
    }
    onDesignOptionsChange(next);
  };

  const previewRef = useRef(null);
  const liveCanvasRef = useRef(null);
  const [liveBgImage, setLiveBgImage] = useState(null);

  // Load background image for live TV canvas (base64/thumb or remote S3 URL via proxy)
  useEffect(() => {
    let cancelled = false;

    const loadBackground = async () => {
      const localSrc =
        designOptions?.backgroundImageBase64 ||
        designOptions?.backgroundImageThumb ||
        null;

      if (localSrc && typeof localSrc === "string" && !localSrc.startsWith("http")) {
        try {
          const img = await loadImageElement(localSrc);
          if (!cancelled) setLiveBgImage(img);
        } catch {
          if (!cancelled) setLiveBgImage(null);
        }
        return;
      }

      const remoteUrl = designOptions?.backgroundImageUrl;
      if (remoteUrl && typeof remoteUrl === "string" && remoteUrl.startsWith("http")) {
        try {
          const dataUrl = await resolveRemoteBackgroundDataUrl(remoteUrl);
          const img = await loadImageElement(dataUrl);
          if (!cancelled) setLiveBgImage(img);
        } catch (err) {
          console.warn("[MenuPreviewPanel] remote background load failed:", err);
          if (!cancelled) setLiveBgImage(null);
        }
        return;
      }

      if (!cancelled) setLiveBgImage(null);
    };

    loadBackground();
    return () => {
      cancelled = true;
    };
  }, [
    designOptions?.backgroundImageBase64,
    designOptions?.backgroundImageThumb,
    designOptions?.backgroundImageUrl,
  ]);

  // Sync parent when fontSize is invalid (e.g. old saved value 10) - use 46 as default
  useEffect(() => {
    if (rawFontSize < 20 || rawFontSize > 60 || Number.isNaN(rawFontSize)) {
      onDesignOptionsChange({ ...designOptions, fontSize: 46 });
    }
  }, []);

  useEffect(() => {
    if (
      sections.length > 0 &&
      !sections.find((s) => s.id === activePreviewSection)
    ) {
      setActivePreviewSection(sections[0].id);
    }
  }, [sections, activePreviewSection]);

  // Live canvas: redraw whenever menu data or design changes (image-editor style)
  useEffect(() => {
    if (!liveCanvasRef.current) return;
    generateImageWithCanvas({
      targetCanvas: liveCanvasRef.current,
      backgroundImageForDraw: liveBgImage,
      topMarginPercent: designOptions?.topMarginPercent,
      pricePositionPercent: designOptions?.pricePositionPercent,
    });
  }, [sections, designOptions, selectedSectionId, liveBgImage]);

  /* ---------- SAMPLE IMAGE (upload to S3 + keep resized base64 for preview, no proxy needed) ---------- */
  const [backgroundImageUploading, setBackgroundImageUploading] =
    useState(false);
  const [savedBackgrounds, setSavedBackgrounds] = useState([]);
  const [savedBackgroundsLoading, setSavedBackgroundsLoading] = useState(false);

  const mergeSavedBackgroundUrls = useCallback(
    (...urlLists) =>
      [...new Set(urlLists.flat().filter((url) => typeof url === "string" && url.startsWith("http")))],
    [],
  );

  const fetchSavedBackgrounds = useCallback(async () => {
    const cid = customerId || localStorage.getItem("customer_id");
    if (!cid) return;
    setSavedBackgroundsLoading(true);
    try {
      const { data } = await axios.post(`${API_BASE}/listMenuBackgrounds`, {
        customerId: cid,
      });
      const fromS3 = (data?.backgrounds || []).map((item) => item.url).filter(Boolean);
      const merged = mergeSavedBackgroundUrls(
        fromS3,
        designOptions?.savedBackgroundUrls,
        designOptions?.backgroundImageUrl ? [designOptions.backgroundImageUrl] : [],
      );
      setSavedBackgrounds(merged);
    } catch (err) {
      console.warn("[MenuPreviewPanel] listMenuBackgrounds failed:", err);
      const merged = mergeSavedBackgroundUrls(
        designOptions?.savedBackgroundUrls,
        designOptions?.backgroundImageUrl ? [designOptions.backgroundImageUrl] : [],
      );
      setSavedBackgrounds(merged);
    } finally {
      setSavedBackgroundsLoading(false);
    }
  }, [
    customerId,
    designOptions?.savedBackgroundUrls,
    designOptions?.backgroundImageUrl,
    mergeSavedBackgroundUrls,
  ]);

  useEffect(() => {
    if (openSections.background) {
      fetchSavedBackgrounds();
    }
  }, [openSections.background, fetchSavedBackgrounds]);

  const applyBackgroundSelection = useCallback(
    (updates, persistMessage) => {
      const next = { ...designOptions, ...updates };
      onDesignOptionsChange(next);
      if (onPersistMenu) {
        Promise.resolve(onPersistMenu(next)).catch(() => { });
      }
      if (persistMessage) message.success(persistMessage);
    },
    [designOptions, onDesignOptionsChange, onPersistMenu],
  );

  const handleSelectSavedBackground = (url) => {
    const savedBackgroundUrls = mergeSavedBackgroundUrls(
      designOptions?.savedBackgroundUrls,
      [url],
    );
    applyBackgroundSelection(
      {
        backgroundImageUrl: url,
        backgroundImageBase64: null,
        backgroundImageThumb: null,
        savedBackgroundUrls,
      },
      "Background applied to TV preview.",
    );
  };

  const [deletingBackgroundUrl, setDeletingBackgroundUrl] = useState(null);

  const handleDeleteSavedBackground = (url) => {
    Modal.confirm({
      title: "Delete saved background?",
      content: "This image will be permanently removed from your saved backgrounds.",
      okText: "Delete",
      okButtonProps: { danger: true },
      cancelText: "Cancel",
      onOk: async () => {
        const cid = customerId || localStorage.getItem("customer_id");
        setDeletingBackgroundUrl(url);
        try {
          await axios.post(`${API_BASE}/deleteMenuBackground`, {
            customerId: cid,
            url,
          });

          // Remove from the visible list.
          setSavedBackgrounds((prev) => prev.filter((u) => u !== url));

          // Drop it from persisted saved urls, and clear it as the active
          // background if it was currently selected.
          const remainingSaved = mergeSavedBackgroundUrls(
            designOptions?.savedBackgroundUrls,
          ).filter((u) => u !== url);
          const updates = { savedBackgroundUrls: remainingSaved };
          if (designOptions?.backgroundImageUrl === url) {
            updates.backgroundImageUrl = null;
            updates.backgroundImageBase64 = null;
            updates.backgroundImageThumb = null;
          }
          applyBackgroundSelection(updates);

          message.success("Background deleted.");
        } catch (err) {
          console.warn("[MenuPreviewPanel] deleteMenuBackground failed:", err);
          message.error(
            err?.response?.data?.message || "Failed to delete background.",
          );
        } finally {
          setDeletingBackgroundUrl(null);
        }
      },
    });
  };
  const resizeImageForPreview = (
    file,
    maxW = 1920,
    maxH = 1080,
    quality = 0.7,
  ) => {
    return new Promise((resolve) => {
      const img = new Image();
      const blobUrl = URL.createObjectURL(file);
      img.onload = () => {
        let w = img.width,
          h = img.height;
        if (w > maxW || h > maxH) {
          const r = Math.min(maxW / w, maxH / h);
          w = Math.round(w * r);
          h = Math.round(h * r);
        }
        const canvas = document.createElement("canvas");
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, w, h);
        URL.revokeObjectURL(blobUrl);
        try {
          resolve(canvas.toDataURL("image/jpeg", quality));
        } catch {
          resolve(null);
        }
      };
      img.onerror = () => {
        URL.revokeObjectURL(blobUrl);
        resolve(null);
      };
      img.src = blobUrl;
    });
  };
  const resizeImageForThumb = (file, maxW = 480, maxH = 270, quality = 0.4) => {
    return new Promise((resolve) => {
      const img = new Image();
      const blobUrl = URL.createObjectURL(file);
      img.onload = () => {
        let w = img.width,
          h = img.height;
        if (w > maxW || h > maxH) {
          const r = Math.min(maxW / w, maxH / h);
          w = Math.round(w * r);
          h = Math.round(h * r);
        }
        const canvas = document.createElement("canvas");
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, w, h);
        URL.revokeObjectURL(blobUrl);
        try {
          resolve(canvas.toDataURL("image/jpeg", quality));
        } catch {
          resolve(null);
        }
      };
      img.onerror = () => {
        URL.revokeObjectURL(blobUrl);
        resolve(null);
      };
      img.src = blobUrl;
    });
  };
  const handleSampleImageUpload = async (file) => {
    console.log("[MenuPreviewPanel] handleSampleImageUpload start", {
      fileName: file?.name,
      type: file?.type,
      size: file?.size,
    });
    if (!file.type.startsWith("image/")) {
      message.error("Please upload an image file.");
      return false;
    }
    const cid = customerId || localStorage.getItem("customer_id");
    if (!cid) {
      const previewBase64 =
        (await resizeImageForPreview(file)) ||
        (await new Promise((r) => {
          const reader = new FileReader();
          reader.onload = () => r(reader.result);
          reader.onerror = () => r(null);
          reader.readAsDataURL(file);
        }));
      const thumb = await resizeImageForThumb(file);
      updateDesignOptions({
        backgroundImageUrl: null,
        backgroundImageBase64: previewBase64,
        backgroundImageThumb: thumb,
      });
      message.warning("Background preview applied locally. Log in and click Save to persist.");
      return false;
    }
    setBackgroundImageUploading(true);
    try {
      const formData = new FormData();
      formData.append("image", file);
      formData.append("customerId", cid);
      const { data } = await axios.post(`${API_BASE}/backgrounds/upload`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const url = data?.url;
      if (data?.success && url) {
        const previewBase64 =
          (await resizeImageForPreview(file)) ||
          (await new Promise((r) => {
            const reader = new FileReader();
            reader.onload = () => r(reader.result);
            reader.onerror = () => r(null);
            reader.readAsDataURL(file);
          }));
        const thumb = await resizeImageForThumb(file);
        console.log("[MenuPreviewPanel] handleSampleImageUpload success", {
          url,
          previewBase64Len: previewBase64?.length,
          thumbLen: thumb?.length,
        });
        const savedBackgroundUrls = mergeSavedBackgroundUrls(
          designOptions?.savedBackgroundUrls,
          [url],
        );
        applyBackgroundSelection(
          {
            backgroundImageUrl: url,
            backgroundImageBase64: previewBase64,
            backgroundImageThumb: thumb,
            savedBackgroundUrls,
          },
          "Background uploaded and saved.",
        );
        fetchSavedBackgrounds();
      } else {
        throw new Error(data?.message || "Upload failed");
      }
    } catch (err) {
      console.error("Background image upload error:", err);
      // Fallback: keep design customization working even if S3 upload fails.
      const previewBase64 =
        (await resizeImageForPreview(file)) ||
        (await new Promise((r) => {
          const reader = new FileReader();
          reader.onload = () => r(reader.result);
          reader.onerror = () => r(null);
          reader.readAsDataURL(file);
        }));
      const thumb = await resizeImageForThumb(file);
      updateDesignOptions({
        backgroundImageUrl: null,
        backgroundImageBase64: previewBase64,
        backgroundImageThumb: thumb,
      });
      const serverReason =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err?.message;
      message.warning(
        serverReason
          ? `Server upload failed (${serverReason}). Applied locally; click Save.`
          : "Server upload failed. Applied locally; click Save.",
      );
    } finally {
      setBackgroundImageUploading(false);
    }
    return false;
  };

  const handleRemoveSampleImage = () => {
    console.log("[MenuPreviewPanel] handleRemoveSampleImage");
    updateDesignOptions({
      backgroundImageUrl: null,
      backgroundImageBase64: null,
      backgroundImageThumb: null,
    });
    message.info("Background image removed. Click Save to persist.");
  };

  /* ---------- PUBLISH TO MODE ---------- */
  const fetchModes = async (options = {}) => {
    const { silent = false, force = false } = options;
    const now = Date.now();
    if (modesFetchInFlightRef.current) return;
    if (!force && modes.length > 0 && now - lastModesFetchAtRef.current < 15000) return;

    modesFetchInFlightRef.current = true;
    setModesLoading(true);
    try {
      const cid = customerId || localStorage.getItem("customer_id");
      if (!cid) {
        if (!silent) {
          message.error({ content: "Please log in to publish.", key: "publish-login-required" });
        }
        return;
      }
      const { data } = await axios.get(`${API_BASE}/getModes`, {
        params: { customer_id: cid },
      });
      setModes(data?.data || []);
      lastModesFetchAtRef.current = Date.now();
    } catch (err) {
      console.error(err);
      if (!silent) {
        message.error({ content: "Failed to load modes.", key: "load-modes-failed" });
      }
    } finally {
      modesFetchInFlightRef.current = false;
      setModesLoading(false);
    }
  };

  useEffect(() => {
    if (showPreviewModal) fetchModes({ force: true });
  }, [showPreviewModal]);

  useEffect(() => {
    if (embedMode) fetchModes({ silent: true });
  }, [embedMode]);

  const fetchPromosInMode = async (modeId) => {
    if (!modeId) {
      setPromosInMode([]);
      setSelectedReplacePromoId(null);
      return;
    }
    setPromosInModeLoading(true);
    try {
      const { data } = await axios.get(`${API_BASE}/fetchPromosByMode/${modeId}`);
      setPromosInMode(data?.success ? data.data || [] : []);
      setSelectedReplacePromoId(null);
    } catch (err) {
      console.error(err);
      setPromosInMode([]);
      setSelectedReplacePromoId(null);
    } finally {
      setPromosInModeLoading(false);
    }
  };

  useEffect(() => {
    if (selectedPublishModeId && publishAction === "replace") {
      fetchPromosInMode(selectedPublishModeId);
    } else {
      setPromosInMode([]);
      setSelectedReplacePromoId(null);
    }
  }, [selectedPublishModeId, publishAction]);

  const handleCreateMode = async () => {
    const name = newModeName.trim();
    if (!name) {
      message.warning("Enter mode name");
      return;
    }
    const cid = customerId || localStorage.getItem("customer_id");
    if (!cid) {
      message.error("Please log in.");
      return;
    }
    setCreateModeLoading(true);
    try {
      const { data } = await axios.post(`${API_BASE}/addMode`, {
        modeName: name,
        customerId: cid,
      });
      if (data?.success !== false) {
        message.success("Mode created!");
        setShowCreateModeModal(false);
        setNewModeName("");
        await fetchModes();
        if (data?.data?._id) setSelectedPublishModeId(data.data._id);
      } else {
        message.error(data?.message || "Failed to create mode.");
      }
    } catch (err) {
      console.error(err);
      message.error("Failed to create mode.");
    } finally {
      setCreateModeLoading(false);
    }
  };

  const handlePublishToMode = async (dataUrlOverride) => {
    if (!selectedPublishModeId) {
      message.warning("Select a mode to publish.");
      return;
    }
    if (publishAction === "replace" && !selectedReplacePromoId) {
      message.warning("Select which menu/image to replace.");
      return;
    }
    const imageUrl = dataUrlOverride ?? previewImageUrl;
    if (!imageUrl) {
      message.error("Missing preview image. Generating…");
      return;
    }
    const cid = customerId || localStorage.getItem("customer_id");
    if (!cid) {
      message.error("Please log in.");
      return;
    }
    const mode = modes.find((m) => m._id === selectedPublishModeId);
    const modeName = mode?.modeName || "mode";
    const title = `Menu - ${activeSelectionLabel || "Menu"}`;
    setAttachLoading(true);
    try {
      const res = await fetch(imageUrl);
      const blob = await res.blob();
      const file = new File([blob], `menu-${Date.now()}.png`, {
        type: "image/png",
      });

      if (publishAction === "replace" && selectedReplacePromoId) {
        const formData = new FormData();
        formData.append("_id", selectedReplacePromoId);
        formData.append("title", title);
        formData.append("customerId", cid);
        formData.append("file", file);
        const { data } = await axios.post(`${API_BASE}/editPromo`, formData, {
          headers: { "Content-Type": "multipart/form-data" },
          timeout: 120000,
        });
        if (data?.success) {
          setPublishedJustNow(true);
          setTimeout(() => setPublishedJustNow(false), 5000);
          message.success("Published — image replaced in \"" + modeName + "\"");
          if (selectedPublishModeId) fetchPromosInMode(selectedPublishModeId);
        } else {
          message.error(data?.message || "Replace failed.");
        }
      } else {
        const formData = new FormData();
        formData.append("file", file);
        formData.append("customerId", cid);
        formData.append("modeId", selectedPublishModeId);
        formData.append("title", title);
        const { data } = await axios.post(`${API_BASE}/addPromoAds`, formData, {
          headers: { "Content-Type": "multipart/form-data" },
          timeout: 120000,
        });
        if (data?.success) {
          setPublishedJustNow(true);
          setTimeout(() => setPublishedJustNow(false), 5000);
          message.success("Published to \"" + modeName + "\"");
        } else {
          message.error(data?.message || "Publish failed.");
        }
      }
    } catch (err) {
      console.error(err);
      message.error("Failed to publish.");
    } finally {
      setAttachLoading(false);
    }
  };

  /* ---------- COMBINED IMAGE: SECTIONS TICKED IN THE "Sections" CARD ---------- */
  // designOptions.combinedSectionIds = section ids in the order the customer ticked them.
  //   0 valid ids -> the single section from `selectedSectionId` (exactly as before)
  //   1 valid id  -> that one section, drawn exactly like a single section
  //   2-3 ids     -> one image split into equal side-by-side areas, one per section (see MULTI-SECTION LAYOUT)
  const combinedSections = (() => {
    const ids = Array.isArray(designOptions?.combinedSectionIds) ? designOptions.combinedSectionIds : [];
    const seen = new Set();
    const out = [];
    for (const id of ids) {
      const key = String(id);
      const sec = sections.find((x) => String(x.id) === key);
      if (sec && !seen.has(key)) {
        seen.add(key);
        out.push(sec);
      }
    }
    return out.slice(0, MAX_COMBINED_SECTIONS);
  })();
  const defaultSelectedSection = sections.find((x) => x.id === selectedSectionId) || sections[0];
  // Ids shown as ticked in the Sections card (the default section counts as ticked until the customer picks others).
  const tickedSectionIds = combinedSections.length
    ? combinedSections.map((x) => String(x.id))
    : defaultSelectedSection
      ? [String(defaultSelectedSection.id)]
      : [];
  // Name used for the published image title and the download file name.
  const activeSelectionLabel =
    combinedSections.length >= 2
      ? combinedSections.map((x) => x.name).join(" & ")
      : combinedSections.length === 1
        ? combinedSections[0].name
        : sections.find((x) => x.id === selectedSectionId)?.name;

  const toggleCombinedSection = (id) => {
    const key = String(id);
    if (tickedSectionIds.includes(key)) {
      if (tickedSectionIds.length <= 1) return; // one section always stays selected
      updateDesignOptions({ combinedSectionIds: tickedSectionIds.filter((x) => x !== key) });
      return;
    }
    if (tickedSectionIds.length >= MAX_COMBINED_SECTIONS) {
      message.info(`You can show up to ${MAX_COMBINED_SECTIONS} sections in one image.`);
      return;
    }
    updateDesignOptions({ combinedSectionIds: [...tickedSectionIds, key] });
  };

  const moveCombinedSection = (id, direction) => {
    const key = String(id);
    const from = tickedSectionIds.indexOf(key);
    const to = from + direction;
    if (from < 0 || to < 0 || to >= tickedSectionIds.length) return;
    const next = [...tickedSectionIds];
    [next[from], next[to]] = [next[to], next[from]];
    updateDesignOptions({ combinedSectionIds: next });
  };

  /* ---------- CANVAS GENERATE ---------- */
  const generateImageWithCanvas = async (options = {}) => {
    const targetCanvas = options.targetCanvas || null;
    const backgroundImageForDraw = options.backgroundImageForDraw ?? null;
    const useLiveDraw = !!targetCanvas;
    const optionsTopMarginPercent = options.topMarginPercent;

    // 2-3 ticked sections -> several sections side by side; otherwise exactly the single-section path.
    const selectedSections =
      combinedSections.length >= 2
        ? combinedSections
        : [combinedSections[0] || sections.find((s) => s.id === selectedSectionId) || sections[0]];
    const selectedSection = selectedSections[0];
    const isMultiSection = selectedSections.length >= 2;
    const hasSelectedItems = isMultiSection
      ? selectedSections.some((s) => s?.items?.length)
      : !!selectedSection?.items?.length;

    if (!useLiveDraw) {
      console.log("[MenuPreviewPanel] generateImageWithCanvas start", {
        fontSize,
        backgroundColor,
        sampleImageUrlType: typeof sampleImageUrl,
        hasBase64: !!designOptions?.backgroundImageBase64,
        hasThumb: !!designOptions?.backgroundImageThumb,
      });
      if (!hasSelectedItems) {
        message.warning("Please add items to the selected section.");
        return;
      }
      setIsGeneratingImage(true);
    }

    try {
      const canvas = targetCanvas || document.createElement("canvas");
      if (!canvas) {
        if (!useLiveDraw) setIsGeneratingImage(false);
        throw new Error("Failed to create canvas element.");
      }

      canvas.width = 1920;
      canvas.height = 1080;
      const ctx = canvas.getContext("2d");

      if (!ctx) {
        if (!useLiveDraw) setIsGeneratingImage(false);
        throw new Error("Failed to get canvas context.");
      }

      /* ---------- BACKGROUND ---------- */
      if (useLiveDraw) {
        ctx.fillStyle = backgroundColor;
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        if (backgroundImageForDraw && backgroundImageForDraw.complete) {
          const img = backgroundImageForDraw;
          const imgWidth = img.width || img.naturalWidth || canvas.width;
          const imgHeight = img.height || img.naturalHeight || canvas.height;
          if (imgWidth > 0 && imgHeight > 0) {
            const imgAspectRatio = imgWidth / imgHeight;
            const canvasAspectRatio = canvas.width / canvas.height;
            let drawWidth, drawHeight, drawX, drawY;
            if (imgAspectRatio > canvasAspectRatio) {
              drawHeight = canvas.height;
              drawWidth = drawHeight * imgAspectRatio;
              drawX = (canvas.width - drawWidth) / 2;
              drawY = 0;
            } else {
              drawWidth = canvas.width;
              drawHeight = drawWidth / imgAspectRatio;
              drawX = 0;
              drawY = (canvas.height - drawHeight) / 2;
            }
            ctx.drawImage(img, drawX, drawY, drawWidth, drawHeight);
          }
        }
      } else if (sampleImageUrl) {
        try {
          // For S3/external URLs: fetch as blob and convert to base64 to avoid CORS with canvas
          let imageSrc = sampleImageUrl;
          if (
            typeof sampleImageUrl === "string" &&
            sampleImageUrl.startsWith("http")
          ) {
            try {
              const proxyUrl = `${API_BASE}/proxyImage?url=${encodeURIComponent(sampleImageUrl)}`;
              const res = await fetch(proxyUrl);
              if (res.ok) {
                const blob = await res.blob();
                imageSrc = await new Promise((resolve, reject) => {
                  const reader = new FileReader();
                  reader.onload = () => resolve(reader.result);
                  reader.onerror = reject;
                  reader.readAsDataURL(blob);
                });
              }
            } catch (fetchErr) {
              console.warn("Proxy fetch failed, trying direct:", fetchErr);
              try {
                const res = await fetch(sampleImageUrl, { mode: "cors" });
                if (res.ok) {
                  const blob = await res.blob();
                  imageSrc = await new Promise((resolve, reject) => {
                    const reader = new FileReader();
                    reader.onload = () => resolve(reader.result);
                    reader.onerror = reject;
                    reader.readAsDataURL(blob);
                  });
                }
                return null;
              } catch (err) {
                console.error("Error fetching image:", err);
                return null;
              }
              imageSrc =
                (await tryFetch(sampleImageUrl)) ||
                (await tryFetch(
                  `https://corsproxy.io/?${encodeURIComponent(sampleImageUrl)}`,
                ));
            }
            if (imageSrc === sampleImageUrl) {
              throw new Error("Could not fetch image");
            }
          }
          // Load and draw sample image as background (imageSrc is base64/data URL)
          const img = new Image();
          await new Promise((resolve, reject) => {
            img.onload = () => {
              ctx.fillStyle = backgroundColor;
              ctx.fillRect(0, 0, canvas.width, canvas.height);
              const imgWidth = img.width || img.naturalWidth || canvas.width;
              const imgHeight =
                img.height || img.naturalHeight || canvas.height;
              if (!imgWidth || !imgHeight || imgWidth <= 0 || imgHeight <= 0) {
                resolve();
                return;
              }
              const imgAspectRatio = imgWidth / imgHeight;
              const canvasAspectRatio = canvas.width / canvas.height;
              let drawWidth, drawHeight, drawX, drawY;
              if (imgAspectRatio > canvasAspectRatio) {
                drawHeight = canvas.height;
                drawWidth = drawHeight * imgAspectRatio;
                drawX = (canvas.width - drawWidth) / 2;
                drawY = 0;
              } else {
                drawWidth = canvas.width;
                drawHeight = drawWidth / imgAspectRatio;
                drawX = 0;
                drawY = (canvas.height - drawHeight) / 2;
              }
              if (drawWidth > 0 && drawHeight > 0) {
                ctx.drawImage(img, drawX, drawY, drawWidth, drawHeight);
              }
              resolve();
            };
            img.onerror = () => {
              message.warning(
                "Failed to load sample image. Using background color instead.",
              );
              ctx.fillStyle = backgroundColor;
              ctx.fillRect(0, 0, canvas.width, canvas.height);
              resolve();
            };
            img.src = imageSrc;
          });
        } catch (err) {
          console.error("Error loading sample image:", err);
          // Fallback to solid background
          ctx.fillStyle = backgroundColor;
          ctx.fillRect(0, 0, canvas.width, canvas.height);
        }
      } else {
        // No sample image - use solid background color
        ctx.fillStyle = backgroundColor;
        ctx.fillRect(0, 0, canvas.width, canvas.height);
      }

      /* ---------- AUTO-DETECT BACKGROUND (when using background image) ---------- */
      let isDarkBackground = false;
      const hasBgImage = sampleImageUrl || (useLiveDraw && backgroundImageForDraw?.complete);
      if (hasBgImage) {
        try {
          const sampleX = Math.floor(canvas.width * 0.5);
          const sampleY = Math.floor(canvas.height * 0.55);
          const sampleSize = 20;
          const centerData = ctx.getImageData(
            Math.max(0, sampleX - sampleSize),
            Math.max(0, sampleY - sampleSize),
            sampleSize * 2,
            sampleSize * 2,
          );
          const centerPixels = centerData.data;
          let totalLuminance = 0;
          let count = 0;
          for (let i = 0; i < centerPixels.length; i += 4) {
            const r = centerPixels[i];
            const g = centerPixels[i + 1];
            const b = centerPixels[i + 2];
            const luminance = 0.299 * r + 0.587 * g + 0.114 * b;
            totalLuminance += luminance;
            count++;
          }
          const avgLuminance = count > 0 ? totalLuminance / count : 128;
          isDarkBackground = avgLuminance < 140;
        } catch (e) {
          isDarkBackground = false;
        }
      }

      /* ---------- CONTENT AREA MARGINS (adapt to background image) ---------- */
      const effectiveTopMarginPercent =
        optionsTopMarginPercent !== undefined && optionsTopMarginPercent !== null
          ? Number(optionsTopMarginPercent)
          : (Number(topMarginPercent) || 0);
      const contentTopPercent = hasBgImage
        ? Math.min(100, Math.max(0, effectiveTopMarginPercent))
        : 0;
      // Additional vertical shift for section title + items when top margin increases.
      const layoutTopOffsetPx = hasBgImage
        ? Math.round((contentTopPercent / 100) * 320)
        : 0;
      const contentLeftMargin = hasBgImage ? 100 : 80;
      const contentRightMargin = contentLeftMargin;
      const contentBottomMargin = hasBgImage ? 20 : 0;

      /* ---------- LIVE DRAW: placeholder when no section/items ---------- */
      if (useLiveDraw && !hasSelectedItems) {
        ctx.fillStyle = backgroundColor;
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        ctx.fillStyle = "#999";
        ctx.font = "28px Arial";
        ctx.textAlign = "center";
        ctx.fillText("Add sections and items to see menu", canvas.width / 2, canvas.height / 2);
        return;
      }

      // Always use Design Options colors (Menu Items, Price Color)
      const effectiveMenuItemsColor = menuItemsColor;
      const effectivePriceColor = priceColor;

      /* ---------- TITLE (Store Name) ---------- */
      // Format store name with proper title case
      const formatTitleCase = (text) => {
        return text
          .toLowerCase()
          .split(" ")
          .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
          .join(" ");
      };

      // Store name - larger, elegant (script-like with italic when available)
      const titleFontSize = Math.max(64, fontSize * 1.8);
      ctx.font = `italic bold ${titleFontSize}px Georgia, ${fontFamily}`;
      ctx.textAlign = "center";

      // Use store hotel name color (reddish for professional look)
      const titleColor = hasBgImage ? "#ffffff" : storeHotelNameColor;

      // Add text shadow for better visibility on image backgrounds
      if (hasBgImage) {
        ctx.shadowColor = "rgba(0, 0, 0, 0.7)";
        ctx.shadowBlur = 4;
        ctx.shadowOffsetX = 2;
        ctx.shadowOffsetY = 2;
      }

      ctx.fillStyle = titleColor;

      // Calculate title position and height (use contentTopPercent when background image)
      // For background layouts, keep top margin in a compact pixel range so
      // item capacity stays close to non-background layout.
      const topMargin = hasBgImage
        ? Math.round(Math.max(20, Math.min(320, (contentTopPercent / 100) * 520)))
        : 60;
      const titleY = topMargin + titleFontSize * 0.75;

      // Disable store-info rendering when a background image is used.
      const shouldRenderStoreInfo = !hasBgImage;

      // Draw store name only when store-info section is active.
      if (storeName && shouldRenderStoreInfo) {
        if (hasBgImage) {
          ctx.shadowColor = "rgba(0, 0, 0, 0.7)";
          ctx.shadowBlur = 4;
          ctx.shadowOffsetX = 2;
          ctx.shadowOffsetY = 2;
        }
        const formattedStoreName = formatTitleCase(storeName);
        ctx.fillText(formattedStoreName, canvas.width / 2, titleY);
        if (!hasBgImage) {
          const titleWidth = ctx.measureText(formattedStoreName).width;
          ctx.strokeStyle = storeHotelNameColor;
          ctx.lineWidth = 2;
          ctx.beginPath();
          ctx.moveTo(canvas.width / 2 - titleWidth / 2 - 10, titleY + 8);
          ctx.lineTo(canvas.width / 2 + titleWidth / 2 + 10, titleY + 8);
          ctx.stroke();
        }
      }

      // Reset shadow
      if (hasBgImage) {
        ctx.shadowColor = "transparent";
        ctx.shadowBlur = 0;
        ctx.shadowOffsetX = 0;
        ctx.shadowOffsetY = 0;
      }

      // Calculate position for address - much closer to title
      let currentY =
        storeName && shouldRenderStoreInfo
          ? titleY + titleFontSize * 0.3
          : topMargin + 20;

      /* ---------- STORE ADDRESS ---------- */
      if (storeAddress && shouldRenderStoreInfo) {
        const addressFontSize = Math.max(26, fontSize * 0.7);
        ctx.font = `${addressFontSize}px ${fontFamily}`;
        ctx.textAlign = "center";
        const addressColor = hasBgImage ? "#ffffff" : storeAddressColor;

        if (hasBgImage) {
          ctx.shadowColor = "rgba(0, 0, 0, 0.6)";
          ctx.shadowBlur = 3;
          ctx.shadowOffsetX = 1;
          ctx.shadowOffsetY = 1;
        }

        ctx.fillStyle = addressColor;
        currentY += Math.max(8, fontSize * 0.15);
        const formattedAddress = formatTitleCase(storeAddress);
        ctx.fillText(formattedAddress, canvas.width / 2, currentY);
        currentY += addressFontSize * 1.0;

        if (hasBgImage) {
          ctx.shadowColor = "transparent";
          ctx.shadowBlur = 0;
          ctx.shadowOffsetX = 0;
          ctx.shadowOffsetY = 0;
        }
      }

      /* ---------- STORE TAGLINE ---------- */
      // Tagline is now drawn in footer bar (reference design) - skip middle bar
      const titleSpacing = Math.max(25, fontSize * 0.5);

      /* ---------- MULTI-SECTION LAYOUT: equal areas ---------- */
      // The width between the side margins is split into one equal slice per selected section, in the order
      // the sections were ticked. Each slice gets its own heading and its own single column of items; a slice
      // never draws outside its own boundaries (see the clip below).
      const MULTI_REGION_PADDING = 10;
      const multiSlotWidth = (canvas.width - contentLeftMargin - contentRightMargin) / Math.max(1, selectedSections.length);
      const multiRegions = isMultiSection
        ? selectedSections.map((section, i) => {
            const slotLeft = contentLeftMargin + i * multiSlotWidth;
            return {
              section,
              slotLeft,
              slotRight: slotLeft + multiSlotWidth,
              left: slotLeft + MULTI_REGION_PADDING,
              right: slotLeft + multiSlotWidth - MULTI_REGION_PADDING,
            };
          })
        : [];

      /* ---------- SECTION NAME ---------- */
      // Respect layout toggle for section name visibility.
      const shouldShowSectionHeader = showSectionHeader;
      let sectionBottomY = currentY + titleSpacing + layoutTopOffsetPx;
      if (isMultiSection) {
        // One heading at the top of each area (same height/position the single heading uses). Always shown:
        // without it the areas could not be told apart. Long names shrink to fit their own area.
        const baseHeadingSize = Math.max(52, fontSize * 1.4);
        const headingY = sectionBottomY + Math.max(20, fontSize * 0.4);
        const headingLineColor = hasBgImage ? "rgba(255, 255, 255, 0.5)" : "#d1d5db";
        multiRegions.forEach(({ section, slotLeft, slotRight, left, right }) => {
          const fullText = String(section?.name || "").toUpperCase();
          if (!fullText) return;
          const maxTextWidth = Math.max(40, right - left - 20);
          let headingSize = baseHeadingSize;
          ctx.font = `bold ${headingSize}px ${fontFamily}`;
          while (headingSize > 24 && ctx.measureText(fullText).width > maxTextWidth) {
            headingSize -= 2;
            ctx.font = `bold ${headingSize}px ${fontFamily}`;
          }
          let shownText = fullText;
          if (ctx.measureText(shownText).width > maxTextWidth) {
            while (shownText.length > 1 && ctx.measureText(`${shownText}…`).width > maxTextWidth) {
              shownText = shownText.slice(0, -1);
            }
            shownText = `${shownText.trimEnd()}…`;
          }
          const centerX = (left + right) / 2;
          const textWidth = ctx.measureText(shownText).width;
          const lineGap = Math.max(12, fontSize * 0.3);
          const lineY = headingY - (headingSize * 0.7) / 2;

          ctx.save();
          ctx.beginPath();
          ctx.rect(slotLeft, 0, slotRight - slotLeft, canvas.height); // heading can't leave its own area
          ctx.clip();
          ctx.textAlign = "center";
          ctx.strokeStyle = headingLineColor;
          ctx.lineWidth = 1.5;
          const leftLineStart = centerX - textWidth / 2 - lineGap;
          const leftLineEnd = Math.max(left, leftLineStart - 120);
          if (leftLineStart - leftLineEnd > 8) {
            ctx.beginPath();
            ctx.moveTo(leftLineEnd, lineY);
            ctx.lineTo(leftLineStart, lineY);
            ctx.stroke();
          }
          const rightLineStart = centerX + textWidth / 2 + lineGap;
          const rightLineEnd = Math.min(right, rightLineStart + 120);
          if (rightLineEnd - rightLineStart > 8) {
            ctx.beginPath();
            ctx.moveTo(rightLineStart, lineY);
            ctx.lineTo(rightLineEnd, lineY);
            ctx.stroke();
          }
          if (hasBgImage) {
            ctx.shadowColor = "rgba(0, 0, 0, 0.7)";
            ctx.shadowBlur = 5;
            ctx.shadowOffsetX = 2;
            ctx.shadowOffsetY = 2;
          }
          ctx.font = `bold ${headingSize}px ${fontFamily}`;
          ctx.fillStyle = sectionNameColorOption || "#000000";
          ctx.fillText(shownText, centerX, headingY);
          ctx.restore();
        });
        sectionBottomY = headingY + baseHeadingSize * 0.4;
      } else if (shouldShowSectionHeader && selectedSection && selectedSection.name) {
        const sectionFontSize = Math.max(52, fontSize * 1.4);
        ctx.font = `bold ${sectionFontSize}px ${fontFamily}`;
        ctx.textAlign = "center";
        const sectionColor =
          sectionNameColorOption || "#000000";

        // Calculate section name Y position (baseline) - more spacing after red bar
        const sectionY = sectionBottomY + Math.max(20, fontSize * 0.4);

        // Measure section text width to position lines
        const sectionText = selectedSection.name.toUpperCase();
        const sectionTextWidth = ctx.measureText(sectionText).width;
        const lineGap = Math.max(20, fontSize * 0.5); // Gap between text and lines

        // Get text metrics to center lines vertically with text
        const textMetrics = ctx.measureText(sectionText);
        // Calculate the vertical center of the text (baseline - half of text height)
        // For uppercase text, approximate height is about 0.7 * fontSize
        const textHeight = sectionFontSize * 0.7;
        const lineY = sectionY - textHeight / 2; // Center lines vertically with text

        // Draw horizontal line BEFORE section name (left side)
        const lineColor = hasBgImage
          ? "rgba(255, 255, 255, 0.5)"
          : "#d1d5db";
        ctx.strokeStyle = lineColor;
        ctx.lineWidth = 1.5;
        ctx.beginPath();
        const leftLineStartX =
          canvas.width / 2 - sectionTextWidth / 2 - lineGap;
        const leftLineEndX = Math.max(100, leftLineStartX - 200); // Extend left, but not too close to edge
        ctx.moveTo(leftLineEndX, lineY);
        ctx.lineTo(leftLineStartX, lineY);
        ctx.stroke();

        // Draw horizontal line AFTER section name (right side)
        ctx.beginPath();
        const rightLineStartX =
          canvas.width / 2 + sectionTextWidth / 2 + lineGap;
        const rightLineEndX = Math.min(
          canvas.width - 100,
          rightLineStartX + 200,
        ); // Extend right, but not too close to edge
        ctx.moveTo(rightLineStartX, lineY);
        ctx.lineTo(rightLineEndX, lineY);
        ctx.stroke();

        if (hasBgImage) {
          ctx.shadowColor = "rgba(0, 0, 0, 0.7)";
          ctx.shadowBlur = 5;
          ctx.shadowOffsetX = 2;
          ctx.shadowOffsetY = 2;
        }

        ctx.fillStyle = sectionColor;
        ctx.fillText(sectionText, canvas.width / 2, sectionY);

        if (hasBgImage) {
          ctx.shadowColor = "transparent";
          ctx.shadowBlur = 0;
          ctx.shadowOffsetX = 0;
          ctx.shadowOffsetY = 0;
        }

        // Calculate section text bottom - for uppercase text, bottom is near baseline
        // Add spacing for text rendering (text extends slightly below baseline)
        const sectionTextBottom = sectionY + sectionFontSize * 0.4;
        sectionBottomY = sectionTextBottom;
      } else {
        // If no section, use title bottom position
        sectionBottomY = currentY + titleSpacing;
      }

      /* ---------- DIVIDER ---------- */
      // Removed divider line to prevent overlap with header
      // Spacing is handled by headerGap in table settings
      const dividerY = sectionBottomY;

      /* ---------- TABLE SETTINGS (Professional two-column layout) ---------- */
      // Use fontSize from Design Options directly - no min override so changes apply
      const itemFontSize = fontSize;
      const priceFontSize = fontSize;
      const textHeight = itemFontSize * 1.2;
      const rowBottomPadding = Math.max(
        0,
        Math.round(itemSpacingPercent * 2.4),
      ); // 0% is tightest; higher values add vertical gap
      const nameToDescriptionGap = 40;
      const descriptionBottomPadding = 1;
      const itemsForLayout = isMultiSection
        ? selectedSections.flatMap((s) => s?.items || [])
        : selectedSection.items || [];
      const hasAnyDescription = showDescription && itemsForLayout.some(
        (i) => i.description && String(i.description).trim()
      );
      const descFontSize =
        descriptionFontSizeOption != null &&
          descriptionFontSizeOption >= 14 &&
          descriptionFontSizeOption <= 40
          ? descriptionFontSizeOption
          : Math.max(18, fontSize * 0.55);
      const descLineHeight = descFontSize * 1.15;
      const effectiveDescriptionColor =
        descriptionColorOption ?? effectiveMenuItemsColor;
      const footerBarHeight = hasBgImage ? 0 : 70;
      const startY = dividerY + 40;

      const availableHeight =
        canvas.height -
        startY -
        footerBarHeight -
        (hasBgImage ? contentBottomMargin : 20);
      // Single-line name/price baseline; spacing slider controls vertical gap.
      const baseRowHeightWithoutDescriptionLines =
        textHeight +
        rowBottomPadding +
        (hasAnyDescription ? nameToDescriptionGap + descriptionBottomPadding : 0);
      const configuredDescriptionLines = hasAnyDescription
        ? Math.max(1, descriptionMaxLines)
        : 0;
      const maxColumns = 4;
      const contentStartX = contentLeftMargin;
      const contentEndX = canvas.width - contentRightMargin;
      const contentWidth = Math.max(260, contentEndX - contentStartX);
      // When `options.pricePositionPercent` is passed explicitly (the live
      // preview effect), it's the RAW stored value and needs decoding. When
      // it's not passed (download/save buttons), fall back to the outer
      // `pricePositionPercent` — that's already fully decoded (and covers
      // the legacy pricePositionPx-only case) — so it must NOT be decoded
      // again here, or any offset above 100 gets corrupted by re-subtracting
      // the legacy-negative encoding.
      const uiOffsetPercent =
        options.pricePositionPercent !== undefined && options.pricePositionPercent !== null
          ? Math.max(
              0,
              Math.min(
                PRICE_OFFSET_MAX_PERCENT,
                decodePricePositionPercent(Number(options.pricePositionPercent)),
              ),
            )
          : Math.max(0, Math.min(PRICE_OFFSET_MAX_PERCENT, Number(pricePositionPercent) || 0));
      // Mirrors draw-time geometry (itemX = col + 14, priceX = itemX + 300 + offset,
      // maxItemWidth = priceX - itemX - 80) so measured name wrapping matches
      // what actually renders for a given column count.
      const measureItemWidthForColumnCount = (count) => {
        const gap = count >= 4 ? 20 : 24;
        const width = (contentWidth - gap * (count - 1)) / count;
        const requestedShift = 300 + Math.round((uiOffsetPercent / 100) * 300);
        const offset =
          count >= 3 ? Math.max(0, Math.min(300, requestedShift)) : requestedShift;
        return Math.max(80, Math.min(width - 28, 300 + offset) - 80);
      };

      /* ---------- COLUMNS: fill col 1 top→bottom, then col 2, 3, 4 (menu add order) ---------- */
      // Multi-section: each area packs its own items below (the shared 4-column packer is not used).
      const sourceItems = isMultiSection ? [] : selectedSection.items || [];

      const normalizeHeaderText = (text) =>
        String(text || "")
          .trim()
          .toLowerCase()
          .replace(/\s+/g, " ");
      const isPriceHeaderLike = (text) => {
        const t = normalizeHeaderText(text);
        if (!t || t.length > 24) return false;
        if (/^(on\s+roll|roll|per\s+lb|per\s+kg|per\s+g|per\s+gm|lb|kg|g)$/.test(t)) {
          return true;
        }
        return false;
      };
      const getItemPriceHeading = (item) => {
        const explicit = String(item?.priceHeading || "").trim();
        if (explicit) return explicit;
        const fromDescription = String(item?.description || "").trim();
        if (isPriceHeaderLike(fromDescription)) return fromDescription;
        return "";

      };
      const wrapTextToWidth = (rawText, maxWidth, maxLines = 2) => {
        const safeText = String(rawText || "").trim();
        if (!safeText) return [""];
        const words = safeText.split(/\s+/);
        const lines = [];
        let line = "";
        for (const word of words) {
          const next = line ? `${line} ${word}` : word;
          if (ctx.measureText(next).width <= maxWidth) {
            line = next;
          } else {
            if (line) lines.push(line);
            if (ctx.measureText(word).width <= maxWidth) {
              line = word;
            } else {
              let chunk = "";
              for (const ch of word) {
                const nextChunk = chunk + ch;
                if (ctx.measureText(nextChunk).width <= maxWidth) {
                  chunk = nextChunk;
                } else {
                  if (chunk) lines.push(chunk);
                  chunk = ch;
                }
              }
              line = chunk;
            }
          }
          if (lines.length >= maxLines) break;
        }
        if (line && lines.length < maxLines) lines.push(line);
        if (lines.length > maxLines) lines.length = maxLines;
        return lines.filter(Boolean);
      };
      const getColumnPriceHeaderForRows = (rows) => {
        const normalizedHeadings = rows
          .map((r) => getItemPriceHeading(r))
          .filter(Boolean)
          .map((h) => normalizeHeaderText(h));
        const uniqueNormalizedHeadings = Array.from(new Set(normalizedHeadings));
        if (uniqueNormalizedHeadings.length !== 1) return "";
        return (
          rows.find(
            (r) =>
              normalizeHeaderText(getItemPriceHeading(r)) ===
              uniqueNormalizedHeadings[0],
          )?.priceHeading ||
          rows.find(
            (r) =>
              normalizeHeaderText(getItemPriceHeading(r)) ===
              uniqueNormalizedHeadings[0],
          )?.description ||
          ""
        );
      };
      const stripHeaderFromDescription = (description, header) => {
        const raw = String(description || "").trim();
        if (!raw) return "";
        const normalizedRaw = normalizeHeaderText(raw);
        const normalizedHeader = normalizeHeaderText(header);
        if (!normalizedHeader) return raw;
        if (normalizedRaw === normalizedHeader) return "";
        if (normalizedRaw.startsWith(`${normalizedHeader} - `)) {
          return raw.slice(raw.indexOf("-") + 1).trim();
        }
        if (normalizedRaw.startsWith(`${normalizedHeader}:`)) {
          return raw.slice(raw.indexOf(":") + 1).trim();
        }
        return raw;
      };
      // Shared by measureItemBlockHeight (column-packing pass) and drawRows
      // (final render pass) so name wrapping / price-overlap detection is
      // computed exactly once, the same way, in both places. `maxItemWidth`
      // is always `priceX - itemX - 80` — that's true both for the
      // column-count estimate (measureItemWidthForColumnCount) and for the
      // real per-column render call (`priceX - itemX - 80` at the drawRows
      // call site) — so `maxItemWidth + 80` reliably reconstructs the real
      // distance from itemX to the price column in either context. Using the
      // same derivation here for both passes is what keeps the height
      // measureItemBlockHeight reserves in sync with what drawRows actually
      // renders.
      const getNameLayout = (name, maxItemWidth) => {
        const numOffset = showItemNumbers ? ctx.measureText("01. ").width : 0;
        const minimumGap = 44;
        const nameX = numOffset;
        const priceXApprox = maxItemWidth + 80;
        const safeNameWidth = showPrices
          ? Math.max(80, priceXApprox - nameX - minimumGap)
          : Math.max(80, maxItemWidth - numOffset);
        const wrappedNameLines = wrapTextToWidth(name, safeNameWidth, 2);
        const lastNameLine = wrappedNameLines[wrappedNameLines.length - 1] || "";
        // Defensive fallback only: safeNameWidth already keeps every wrapped
        // line clear of the price column, so this should not trigger in
        // normal use — it only guards against an extreme edge case (e.g. an
        // unbroken string of characters wider than safeNameWidth).
        const isPriceOverlapping =
          showPrices &&
          nameX + ctx.measureText(lastNameLine).width + minimumGap > priceXApprox;
        return { numOffset, safeNameWidth, wrappedNameLines, isPriceOverlapping };
      };

      const measureItemBlockHeight = (
        item,
        maxItemWidth,
        columnPriceHeader,
        descLineLimit,
      ) => {

        ctx.font = `${itemNameBold ? "bold" : "normal"} ${itemFontSize}px ${fontFamily}`;
        const name = (item.name || "")
          .toLowerCase()
          .split(" ")
          .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
          .join(" ");
        const { wrappedNameLines, isPriceOverlapping } = getNameLayout(
          name,
          maxItemWidth,
        );
        // Height reflects the name lines actually rendered; priceExtra only
        // adds a line in the rare case getNameLayout's overlap fallback
        // fires — which drawRows below checks identically, so this height
        // always matches what gets drawn.
        const nameHeight = textHeight * Math.max(1, wrappedNameLines.length);
        const priceExtra = isPriceOverlapping ? textHeight : 0;
        let blockHeight = Math.max(nameHeight, textHeight + priceExtra);
        const cleanedDescription = stripHeaderFromDescription(
          item.description,
          columnPriceHeader,
        );

        if (showDescription && cleanedDescription && descLineLimit > 0) {
          blockHeight +=
            nameToDescriptionGap +
            descLineHeight * descLineLimit +
            descriptionBottomPadding;
        }
        return blockHeight + rowBottomPadding;
      };
      // Flow items top→bottom per column using each item's real measured
      // height; when the next item would cross the column bottom (minus a
      // small safety margin), it moves to the top of the next column.
      const columnBottomMargin = 16;
      const buildHeightAwareColumnMeta = (rowsToPack, measureWidth) => {
        const columnBottomY = startY + availableHeight - columnBottomMargin;
        const columnHeaderHeight = (firstItem) => {
          if (!showPrices) return 0;
          return getColumnPriceHeaderForRows([firstItem]) ? textHeight * 1.35 : 0;
        };

        const packed = [];
        let currentRows = [];
        let cursorY = startY;

        for (const item of rowsToPack) {
          // All columns used: remaining items append to the last column.
          if (packed.length >= maxColumns && currentRows.length === 0) {
            packed[packed.length - 1].push(item);
            continue;
          }

          const headerH = currentRows.length === 0 ? columnHeaderHeight(item) : 0;
          const tentativeHeader = currentRows.length
            ? getColumnPriceHeaderForRows(currentRows)
            : getColumnPriceHeaderForRows([item]);
          const blockH = measureItemBlockHeight(
            item,
            measureWidth,
            tentativeHeader,
            configuredDescriptionLines,
          );
          const nextBottom =
            (currentRows.length === 0 ? startY + headerH : cursorY) + blockH;

          if (currentRows.length > 0 && nextBottom > columnBottomY) {
            packed.push(currentRows);
            currentRows = [];
            if (packed.length >= maxColumns) {
              // No more columns available; append remaining items to the last one.
              packed[packed.length - 1].push(item);
              cursorY = startY;
              continue;
            }
            const newHeaderH = columnHeaderHeight(item);
            const newBlockH = measureItemBlockHeight(
              item,
              measureWidth,
              getColumnPriceHeaderForRows([item]),
              configuredDescriptionLines,
            );
            currentRows = [item];
            cursorY = startY + newHeaderH + newBlockH;
            continue;
          }

          cursorY = currentRows.length === 0 ? startY + headerH + blockH : cursorY + blockH;
          currentRows.push(item);
        }

        if (currentRows.length) packed.push(currentRows);

        let runningBaseIndex = 0;
        return packed.map((rows) => {
          const meta = { rows, baseIndex: runningBaseIndex };
          runningBaseIndex += rows.length;
          return meta;
        });
      };

      // Try 1 column first; only add another column when the previous ones
      // are actually full at that layout's real column width.
      let columnMeta = [];
      for (let candidate = 1; candidate <= maxColumns; candidate++) {
        columnMeta = buildHeightAwareColumnMeta(
          sourceItems,
          measureItemWidthForColumnCount(candidate),
        );
        if (columnMeta.length <= candidate) break;
      }

      const columnCount = Math.max(1, columnMeta.length);
      const columnGap = columnCount >= 4 ? 20 : 24;
      const columnWidth =
        (contentWidth - columnGap * (columnCount - 1)) / columnCount;
      const columnItems = columnMeta.map((col) => col.rows);

      // After columns are fixed, use remaining vertical space inside each column
      // to increase description lines before pushing content to next column.
      const fullestColumnRows = Math.max(
        1,
        ...columnItems.map((col) => col.length || 0),
      );
      const spacePerRowInFullestColumn = availableHeight / fullestColumnRows;
      const autoDescriptionLinesBySpace = hasAnyDescription
        ? Math.max(
          1,
          Math.floor(
            (spacePerRowInFullestColumn - baseRowHeightWithoutDescriptionLines) /
            descLineHeight,
          ),
        )
        : 0;
      // User "Max lines" is the cap; auto space only reduces when the column is tight.
      const descriptionLinesForLayout = hasAnyDescription
        ? Math.min(
          configuredDescriptionLines,
          Math.max(1, Math.min(5, autoDescriptionLinesBySpace)),
        )
        : 0;
      /* ---------- ROWS (Professional layout: item left, price right) ---------- */
      const drawRows = (
        rows,
        baseIndex,
        itemX,
        priceX,
        maxItemWidth,
        columnEndX,
        priceHeaderText,
      ) => {
        const columnPriceHeader = showPrices
          ? String(priceHeaderText || "").trim()
          : "";

        const headerOffsetY = columnPriceHeader ? textHeight * 1.35 : 0;

        if (showPrices && columnPriceHeader) {
          ctx.textAlign = "right";
          ctx.font = `bold ${priceFontSize}px ${fontFamily}`;
          ctx.fillStyle = effectivePriceColor;
          ctx.fillText(columnPriceHeader, priceX, startY + textHeight * 0.45);
        }

        let rowCursor = startY + headerOffsetY;

        rows.forEach((item, index) => {
          const blockHeight = measureItemBlockHeight(
            item,
            maxItemWidth,
            columnPriceHeader,
            descriptionLinesForLayout,
          );

          const y = rowCursor + textHeight;
          console.log("y", y);
          if (hasBgImage) {
            ctx.shadowColor = "transparent";
            ctx.shadowBlur = 0;
            ctx.shadowOffsetX = 0;
            ctx.shadowOffsetY = 0;
          }

          ctx.textAlign = "left";
          ctx.font = `${itemNameBold ? "bold" : "normal"} ${itemFontSize}px ${fontFamily}`;
          if (hasBgImage && !isDarkBackground) {
            ctx.shadowColor = "rgba(0, 0, 0, 0.5)";
            ctx.shadowBlur = 2;
            ctx.shadowOffsetX = 1;
            ctx.shadowOffsetY = 1;
          }

          if (showItemNumbers) {
            const itemNumber = String(baseIndex + index + 1).padStart(2, "0");
            ctx.fillStyle = itemNumberColor;
            ctx.fillText(`${itemNumber}. `, itemX, y);
          }

          ctx.fillStyle = effectiveMenuItemsColor;
          // Title case: first letter capital, others small
          let name = (item.name || "")
            .toLowerCase()
            .split(" ")
            .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
            .join(" ");
          // Same helper measureItemBlockHeight used to size this row — keeps
          // wrapping and the overlap fallback identical between the measured
          // height and what's actually drawn.
          const { numOffset, wrappedNameLines, isPriceOverlapping } = getNameLayout(
            name,
            maxItemWidth,
          );
          const nameX = itemX + numOffset;
          wrappedNameLines.forEach((line, lineIndex) => {
            ctx.fillText(line, nameX, y + lineIndex * textHeight);
          });

          const priceY = isPriceOverlapping ? y + textHeight : y;
          const maxContentY = Math.max(
            y + (wrappedNameLines.length - 1) * textHeight,
            showPrices ? priceY : y + (wrappedNameLines.length - 1) * textHeight,
          );

          // Description - optional, directly below item name in same column only
          const cleanedDescription = stripHeaderFromDescription(
            item.description,
            columnPriceHeader,
          );
          console.log("cleanedDescription", cleanedDescription);
          if (showDescription && cleanedDescription) {
            ctx.textAlign = "left";
            ctx.font = `normal ${descFontSize}px ${fontFamily}`;
            ctx.fillStyle = effectiveDescriptionColor;
            const descStartX = itemX + numOffset;
            // Keep description inside current column and out of the price area.
            const availableInColumn = Math.max(80, columnEndX - descStartX);
            const availableWidthToPrice = showPrices
              ? Math.max(80, priceX - descStartX - 80)
              : availableInColumn;
            const descMaxWidth = Math.max(
              80,
              Math.min(
                (Math.min(availableWidthToPrice, availableInColumn) * descriptionWidthPercent) / 100,
                availableInColumn
              )
            );
            const rawDesc = cleanedDescription;
            console.log("rawDesc", rawDesc);

            let descLines;
            if (descriptionWrap) {
              const words = rawDesc.split(/\s+/);
              const lines = [];
              let line = "";
              for (const word of words) {
                const next = line ? `${line} ${word}` : word;
                if (ctx.measureText(next).width <= descMaxWidth) {
                  line = next;
                } else {
                  if (line) lines.push(line);
                  line = ctx.measureText(word).width <= descMaxWidth ? word : "";
                  if (!line && word) {
                    let w = word;
                    while (w && ctx.measureText(w + "...").width > descMaxWidth) w = w.slice(0, -1);
                    if (w) line = w + "...";
                  }
                }
              }
              if (line) lines.push(line);
              descLines = lines.slice(0, descriptionLinesForLayout);
              if (lines.length > descriptionLinesForLayout && descLines.length > 0) {
                let last = descLines[descLines.length - 1];
                if (ctx.measureText(last + "...").width <= descMaxWidth) last += "...";
                descLines[descLines.length - 1] = last;
              }
            } else {
              let single = rawDesc;
              if (ctx.measureText(single).width > descMaxWidth) {
                while (single.length && ctx.measureText(single + "...").width > descMaxWidth)
                  single = single.slice(0, -1);
                single = single + "...";
              }
              descLines = single ? [single] : [];
            }
            const descY = maxContentY + nameToDescriptionGap;
            descLines.forEach((ln, i) => {
              ctx.fillText(ln, descStartX, descY + i * descLineHeight);
            });
          }

          if (showPrices) {
            ctx.textAlign = "right";
            ctx.font = `bold ${priceFontSize}px ${fontFamily}`;
            ctx.fillStyle = effectivePriceColor;
            ctx.fillText(formatItemCanvasPrice(item, formatPrice), priceX, priceY);
          }

          if (hasBgImage && !isDarkBackground) {
            ctx.shadowColor = "transparent";
            ctx.shadowBlur = 0;
            ctx.shadowOffsetX = 0;
            ctx.shadowOffsetY = 0;
          }

          rowCursor += blockHeight;
        });
      };

      // 0% starts from the previous 200% position (300px shift),
      // then increases further right as percentage grows.
      const requestedRightShiftPx = 300 + Math.round((uiOffsetPercent / 100) * 300);
      const effectivePriceOffset =
        columnCount >= 3
          ? Math.max(0, Math.min(300, requestedRightShiftPx))
          : requestedRightShiftPx;

      columnMeta.forEach((columnData, columnIndex) => {
        const rows = columnData.rows;
        if (!rows.length) return;
        const colStartX = contentStartX + columnIndex * (columnWidth + columnGap);
        const colEndX = colStartX + columnWidth;
        const itemX = colStartX + 14;
        // Keep default gap at 300px between item name and price.
        // Offset control starts at 0 and moves price farther right.
        const basePriceGap = 300;
        const priceX = Math.min(colEndX - 14, itemX + basePriceGap + effectivePriceOffset);
        const normalizedHeadings = rows
          .map((r) => getItemPriceHeading(r))
          .filter(Boolean)
          .map((h) => normalizeHeaderText(h));
        const uniqueNormalizedHeadings = Array.from(new Set(normalizedHeadings));
        const columnPriceHeader = showPrices &&
          uniqueNormalizedHeadings.length === 1
          ? rows.find((r) => normalizeHeaderText(getItemPriceHeading(r)) === uniqueNormalizedHeadings[0])?.priceHeading ||
          rows.find((r) => normalizeHeaderText(getItemPriceHeading(r)) === uniqueNormalizedHeadings[0])?.description ||
          ""
          : "";
        drawRows(
          rows,
          columnData.baseIndex,
          itemX,
          priceX,
          priceX - itemX - 80,
          colEndX - 10,
          columnPriceHeader,
        );
      });

      /* ---------- MULTI-SECTION ITEMS: one column per area, items that don't fit are left out ---------- */
      if (isMultiSection) {
        const areaBottomY = startY + availableHeight; // below this is the footer / bottom margin
        const columnBottomY = areaBottomY - columnBottomMargin;
        multiRegions.forEach(({ section, slotLeft, slotRight, left, right }) => {
          const sectionItems = section?.items || [];
          if (!sectionItems.length) return;
          const itemX = left + 14;
          const priceX = right - 14; // prices line up at the right edge of their own area
          const maxItemWidth = priceX - itemX - 80;

          // Take items in order while they still fit above the bottom of the area; stop at the first one that
          // doesn't (later items are not pulled up, so the order is kept). Heights come from the same
          // measureItemBlockHeight that drawRows uses, so what fits here is exactly what gets drawn.
          const rows = [];
          for (const item of sectionItems) {
            const candidate = [...rows, item];
            const header = showPrices ? getColumnPriceHeaderForRows(candidate) : "";
            let used = header ? textHeight * 1.35 : 0;
            for (const row of candidate) {
              used += measureItemBlockHeight(row, maxItemWidth, header, descriptionLinesForLayout);
            }
            if (startY + used > columnBottomY) break;
            rows.push(item);
          }
          if (!rows.length) return;

          ctx.save();
          ctx.beginPath();
          ctx.rect(slotLeft, startY - 10, slotRight - slotLeft, areaBottomY - startY + 10); // never into a neighbour or the footer
          ctx.clip();
          drawRows(
            rows,
            0, // numbering restarts in every area
            itemX,
            priceX,
            maxItemWidth,
            right - 10,
            showPrices ? getColumnPriceHeaderForRows(rows) : "",
          );
          ctx.restore();
        });
      }

      /* ---------- FOOTER BAR ---------- */
      if (!hasBgImage && footerBarHeight > 0) {
        const footerY = canvas.height - footerBarHeight;
        ctx.fillStyle = "#8B2500";
        ctx.fillRect(0, footerY, canvas.width, footerBarHeight);

        if (storeTagline) {
          const footerFontSize = Math.max(24, fontSize * 0.6);
          ctx.font = `bold ${footerFontSize}px ${fontFamily}`;
          ctx.textAlign = "center";
          ctx.fillStyle = taglineColor;
          let footerText = storeTagline.trim();
          if (footerText.includes(",")) {
            footerText = footerText
              .split(",")
              .map((p) => p.trim())
              .filter(Boolean)
              .join(" | ");
          }
          ctx.fillText(
            footerText.toUpperCase(),
            canvas.width / 2,
            footerY + footerBarHeight / 2 + footerFontSize * 0.35,
          );
        }
      }

      /* ---------- PREVIEW MODAL (only when generating for download, not live canvas) ---------- */
      if (!useLiveDraw) {
        const dataUrl = canvas.toDataURL("image/png");
        setPreviewImageUrl(dataUrl);
        if (!options.skipModal) {
          setShowPreviewModal(true);
          message.success("Preview ready!");
        }
        setIsGeneratingImage(false);
        return dataUrl;
      }
    } catch (err) {
      if (!useLiveDraw) {
        console.error("Error generating menu image:", err);
        message.error(
          `Failed to generate menu image: ${err.message || "Unknown error"}`,
        );
        setIsGeneratingImage(false);
      }
    }
  };

  const currentSection =
    sections.find((s) => s.id === activePreviewSection) || sections[0];

  return (
    <>
      <div className={`w-full min-w-0 overflow-x-hidden ${embedMode ? "" : "min-h-screen bg-gray-100"}`}>

        {/* ── TWO COLUMN LAYOUT ──────────────── */}
        <div className={`flex flex-col lg:flex-row gap-4 w-full min-w-0 ${embedMode ? "p-3 sm:p-4" : "p-3 sm:p-4"}`}>

          {/* ════════════════════════════════════
            LEFT PANEL — Controls
        ════════════════════════════════════ */}
          <div className="w-full lg:w-80 lg:flex-shrink-0 flex flex-col gap-3 min-w-0">

            {/* ── SECTIONS: tick 2-3 to show them side by side in one image ── */}
            {sections.length > 1 && (
              <div className="bg-white rounded-2xl shadow-sm border border-gray-100 px-4 py-3">
                <p className="text-sm font-bold text-gray-900">Sections</p>
                <p className="text-xs text-gray-500 mt-0.5 mb-2">
                  Tick up to {MAX_COMBINED_SECTIONS} sections to show them side by side in one image, each in an equal share of the width.
                </p>
                <ul className="space-y-1">
                  {sections.map((sec) => {
                    const key = String(sec.id);
                    const position = tickedSectionIds.indexOf(key);
                    const ticked = position !== -1;
                    return (
                      <li key={sec.id} className="flex items-center gap-2">
                        <label className="flex items-center gap-2 flex-1 min-w-0 cursor-pointer py-1">
                          <input
                            type="checkbox"
                            checked={ticked}
                            onChange={() => toggleCombinedSection(sec.id)}
                            className="h-4 w-4 flex-shrink-0 accent-blue-600"
                          />
                          <span className="text-sm text-gray-800 truncate">{sec.name}</span>
                          <span className="text-xs text-gray-400 flex-shrink-0">({(sec.items || []).length})</span>
                        </label>
                        {ticked && tickedSectionIds.length > 1 && (
                          <span className="flex items-center gap-0.5 flex-shrink-0">
                            <span className="text-[11px] font-bold text-blue-600 w-4 text-center">{position + 1}</span>
                            <button
                              type="button"
                              onClick={() => moveCombinedSection(sec.id, -1)}
                              disabled={position === 0}
                              aria-label={`Move ${sec.name} earlier`}
                              className="w-6 h-6 rounded border border-gray-200 text-xs text-gray-600 disabled:opacity-30"
                            >
                              ▲
                            </button>
                            <button
                              type="button"
                              onClick={() => moveCombinedSection(sec.id, 1)}
                              disabled={position === tickedSectionIds.length - 1}
                              aria-label={`Move ${sec.name} later`}
                              className="w-6 h-6 rounded border border-gray-200 text-xs text-gray-600 disabled:opacity-30"
                            >
                              ▼
                            </button>
                          </span>
                        )}
                      </li>
                    );
                  })}
                </ul>
              </div>
            )}

            {/* ── BACKGROUND IMAGE ───────────── */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
              <button
                type="button"
                onClick={() => toggleSection("background")}
                className="w-full flex items-center justify-between px-4 py-3 hover:bg-gray-50 transition-colors"
              >
                <div className="flex items-center gap-2">
                  <ImageIcon size={15} className="text-blue-600" />
                  <span className="text-sm font-bold text-gray-900">Background Image</span>
                  {sampleImageUrl && (
                    <span className="w-2 h-2 bg-blue-600 rounded-full flex-shrink-0" />
                  )}
                </div>
                {openSections.background ? (
                  <ChevronUp size={15} className="text-gray-400" />
                ) : (
                  <ChevronDown size={15} className="text-gray-400" />
                )}
              </button>

              {openSections.background && (
                <div className="px-4 pb-4 border-t border-gray-50">
                  <div className="pt-3">
                    <Upload
                      beforeUpload={handleSampleImageUpload}
                      showUploadList={false}
                      disabled={backgroundImageUploading}
                      className="w-full block"
                    >
                      <button
                        type="button"
                        disabled={backgroundImageUploading}
                        className="w-full flex items-center justify-center gap-2 border border-dashed border-gray-300 rounded-xl py-2.5 text-sm font-medium text-blue-600 hover:border-blue-300 hover:bg-blue-50 transition-colors disabled:opacity-50"
                      >
                        <UploadIcon size={14} />
                        {backgroundImageUploading ? "Uploading…" : sampleImageUrl ? "Change image" : "Upload background"}
                      </button>
                    </Upload>
                    {sampleImageUrl && (
                      <button
                        type="button"
                        onClick={handleRemoveSampleImage}
                        className="mt-2 text-xs text-red-500 font-medium flex items-center gap-1"
                      >
                        <Trash2 size={12} /> Remove background
                      </button>
                    )}

                    {(savedBackgrounds.length > 0 || savedBackgroundsLoading) && (
                      <div className="mt-3">
                        <p className="text-xs font-semibold text-gray-600 mb-2">
                          Saved backgrounds
                        </p>
                        {savedBackgroundsLoading ? (
                          <p className="text-xs text-gray-400">Loading saved images…</p>
                        ) : (
                          <div className="grid grid-cols-3 gap-2">
                            {savedBackgrounds.map((url) => {
                              const isSelected = designOptions?.backgroundImageUrl === url;
                              const isDeleting = deletingBackgroundUrl === url;
                              return (
                                <div
                                  key={url}
                                  role="button"
                                  tabIndex={0}
                                  onClick={() => handleSelectSavedBackground(url)}
                                  onKeyDown={(e) => {
                                    if (e.key === "Enter" || e.key === " ") {
                                      e.preventDefault();
                                      handleSelectSavedBackground(url);
                                    }
                                  }}
                                  className={`group relative rounded-lg overflow-hidden border-2 aspect-video cursor-pointer ${isSelected
                                    ? "border-blue-600 ring-2 ring-blue-200"
                                    : "border-gray-200 hover:border-blue-300"
                                    } ${isDeleting ? "opacity-50 pointer-events-none" : ""}`}
                                  title="Use this background"
                                >
                                  <img
                                    src={`${API_BASE}/proxyImage?url=${encodeURIComponent(url)}`}
                                    alt="Saved background"
                                    className="w-full h-full object-cover"
                                  />

                                  {isSelected && (
                                    <span className="absolute bottom-1 left-1 bg-blue-600 text-white text-[9px] font-semibold px-1.5 py-0.5 rounded">
                                      Selected
                                    </span>
                                  )}

                                  <button
                                    type="button"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleDeleteSavedBackground(url);
                                    }}
                                    disabled={isDeleting}
                                    title="Delete background"
                                    className="absolute top-1 right-1 z-10 flex items-center justify-center w-6 h-6 rounded-full bg-red-600 text-white shadow-md hover:bg-red-700 transition-colors disabled:opacity-50"
                                  >
                                    <Trash2 size={13} />
                                  </button>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* ── STORE INFORMATION ──────────── */}
            {!sampleImageUrl && (
              <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
                <button
                  type="button"
                  onClick={() => toggleSection("storeInfo")}
                  className="w-full flex items-center justify-between px-4 py-3 hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-center gap-2">
                    <Settings2 size={15} className="text-blue-600" />
                    <span className="text-sm font-bold text-gray-900">Store Information</span>
                  </div>
                  {openSections.storeInfo ? (
                    <ChevronUp size={15} className="text-gray-400" />
                  ) : (
                    <ChevronDown size={15} className="text-gray-400" />
                  )}
                </button>

                {openSections.storeInfo && (
                  <div className="px-4 pb-4 border-t border-gray-50 pt-3 space-y-3">
                    {/* Store name */}
                    <div>
                      <label className="text-xs text-gray-500 font-medium mb-1 block">
                        Store / Hotel name
                      </label>
                      <div className="flex gap-2">
                        <input
                          type="text"
                          placeholder="Enter store name"
                          value={storeName}
                          onChange={(e) => updateDesignOptions({ storeName: e.target.value })}
                          className="flex-1 border border-gray-200 rounded-xl px-3 py-2 text-sm text-gray-900 outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 min-w-0"
                        />
                        <input
                          type="color"
                          value={storeHotelNameColor}
                          onChange={(e) => updateDesignOptions({ storeHotelNameColor: e.target.value })}
                          className="w-9 h-9 p-1 rounded-lg border border-gray-200 cursor-pointer flex-shrink-0"
                          title="Name color"
                        />
                      </div>
                    </div>

                    {/* Address */}
                    <div>
                      <label className="text-xs text-gray-500 font-medium mb-1 block">
                        Address<span className="text-gray-400 font-normal ml-1">(optional)</span>
                      </label>
                      <div className="flex gap-2">
                        <input
                          type="text"
                          placeholder="Enter store address"
                          value={storeAddress}
                          onChange={(e) => updateDesignOptions({ storeAddress: e.target.value })}
                          className="flex-1 border border-gray-200 rounded-xl px-3 py-2 text-sm text-gray-900 outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 min-w-0"
                        />
                        <input
                          type="color"
                          value={storeAddressColor}
                          onChange={(e) => updateDesignOptions({ storeAddressColor: e.target.value })}
                          className="w-9 h-9 p-1 rounded-lg border border-gray-200 cursor-pointer flex-shrink-0"
                          title="Address color"
                        />
                      </div>
                    </div>

                    {/* Tagline */}
                    <div>
                      <label className="text-xs text-gray-500 font-medium mb-1 block">
                        Tagline<span className="text-gray-400 font-normal ml-1">(red footer bar)</span>
                      </label>
                      <div className="flex gap-2">
                        <input
                          type="text"
                          placeholder="e.g. FRESH FISH • DAILY STOCK"
                          value={storeTagline}
                          onChange={(e) => updateDesignOptions({ storeTagline: e.target.value })}
                          className="flex-1 border border-gray-200 rounded-xl px-3 py-2 text-sm text-gray-900 outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 min-w-0"
                        />
                        <input
                          type="color"
                          value={taglineColor}
                          onChange={(e) => updateDesignOptions({ taglineColor: e.target.value })}
                          className="w-9 h-9 p-1 rounded-lg border border-gray-200 cursor-pointer flex-shrink-0"
                          title="Tagline color"
                        />
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* ── TOGGLES ─────────────────────── */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 px-4 py-3 space-y-2.5">
              {/* Show item numbers */}
              <label className="flex items-center justify-between cursor-pointer">
                <span className="text-sm font-medium text-gray-800">Show item numbers</span>
                <button
                  type="button"
                  role="switch"
                  aria-checked={showItemNumbers}
                  onClick={() => updateDesignOptions({ showItemNumbers: !showItemNumbers })}
                  className={`relative inline-flex items-center w-11 h-6 rounded-full border-2 transition-all focus:outline-none ${showItemNumbers ? "bg-blue-600 border-blue-600" : "bg-white border-gray-300"}`}
                >
                  <span className={`inline-block w-4 h-4 bg-white rounded-full shadow-md transform transition-transform ${showItemNumbers ? "translate-x-5" : "translate-x-0.5 bg-gray-400"}`} />
                </button>
              </label>

              {/* Show descriptions */}
              {sections.some((s) => (s.items || []).some((i) => i.description && String(i.description).trim())) && (
                <label className="flex items-center justify-between cursor-pointer">
                  <span className="text-sm font-medium text-gray-800">Show descriptions</span>
                  <button
                    type="button"
                    role="switch"
                    aria-checked={showDescription}
                    onClick={() => updateDesignOptions({ showDescription: !showDescription })}
                    className={`relative inline-flex items-center w-11 h-6 rounded-full border-2 transition-all focus:outline-none ${showDescription ? "bg-blue-600 border-blue-600" : "bg-white border-gray-300"}`}
                  >
                    <span className={`inline-block w-4 h-4 bg-white rounded-full shadow-md transform transition-transform ${showDescription ? "translate-x-5" : "translate-x-0.5 bg-gray-400"}`} />
                  </button>
                </label>
              )}

              {/* Item name bold */}
              <label className="flex items-center justify-between cursor-pointer">
                <span className="text-sm font-medium text-gray-800">Bold item names</span>
                <button
                  type="button"
                  role="switch"
                  aria-checked={itemNameBold}
                  onClick={() => updateDesignOptions({ itemNameBold: !itemNameBold })}
                  className={`relative inline-flex items-center w-11 h-6 rounded-full border-2 transition-all focus:outline-none ${itemNameBold ? "bg-blue-600 border-blue-600" : "bg-white border-gray-300"}`}
                >
                  <span className={`inline-block w-4 h-4 bg-white rounded-full shadow-md transform transition-transform ${itemNameBold ? "translate-x-5" : "translate-x-0.5 bg-gray-400"}`} />
                </button>
              </label>

              {/* Hide prices */}
              <label className="flex items-center justify-between cursor-pointer">
                <span className="text-sm font-medium text-gray-800">Hide prices</span>
                <button
                  type="button"
                  role="switch"
                  aria-checked={hidePrices}
                  onClick={() => updateDesignOptions({ hidePrices: !hidePrices })}
                  className={`relative inline-flex items-center w-11 h-6 rounded-full border-2 transition-all focus:outline-none ${hidePrices ? "bg-blue-600 border-blue-600" : "bg-white border-gray-300"}`}
                >
                  <span className={`inline-block w-4 h-4 bg-white rounded-full shadow-md transform transition-transform ${hidePrices ? "translate-x-5" : "translate-x-0.5 bg-gray-400"}`} />
                </button>
              </label>

              {/* Show section header */}
              <label className="flex items-center justify-between cursor-pointer">
                <span className="text-sm font-medium text-gray-800">Show section name</span>
                <button
                  type="button"
                  role="switch"
                  aria-checked={showSectionHeader}
                  onClick={() => updateDesignOptions({ showSectionHeader: !showSectionHeader })}
                  className={`relative inline-flex items-center w-11 h-6 rounded-full border-2 transition-all focus:outline-none ${showSectionHeader ? "bg-blue-600 border-blue-600" : "bg-white border-gray-300"}`}
                >
                  <span className={`inline-block w-4 h-4 bg-white rounded-full shadow-md transform transition-transform ${showSectionHeader ? "translate-x-5" : "translate-x-0.5 bg-gray-400"}`} />
                </button>
              </label>
            </div>

            {/* ── TYPOGRAPHY ──────────────────── */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
              <button
                type="button"
                onClick={() => toggleSection("typography")}
                className="w-full flex items-center justify-between px-4 py-3 hover:bg-gray-50 transition-colors"
              >
                <div className="flex items-center gap-2">
                  <Type size={15} className="text-blue-600" />
                  <span className="text-sm font-bold text-gray-900">Typography</span>
                </div>
                {openSections.typography ? (
                  <ChevronUp size={15} className="text-gray-400" />
                ) : (
                  <ChevronDown size={15} className="text-gray-400" />
                )}
              </button>

              {openSections.typography && (
                <div className="px-4 pb-4 border-t border-gray-50 pt-3 space-y-3">
                  {/* Font size */}
                  <div>
                    <label className="text-xs text-gray-500 font-medium mb-1 block">
                      Font size (20–60px)
                    </label>
                    <NumericTextInput
                      value={fontSize}
                      min={20}
                      max={60}
                      suffix="px"
                      fallback={30}
                      onChange={(next) => updateDesignOptions({ fontSize: next })}
                    />
                  </div>

                  {/* Font family */}
                  <div>
                    <label className="text-xs text-gray-500 font-medium mb-1 block">
                      Font style
                    </label>
                    <select
                      value={fontFamily}
                      onChange={(e) => updateDesignOptions({ fontFamily: e.target.value })}
                      className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm text-gray-900 outline-none focus:border-blue-500 bg-white"
                    >
                      <option value="Arial">Arial</option>
                      <option value="Helvetica">Helvetica</option>
                      <option value="Times New Roman">Times New Roman</option>
                      <option value="Georgia">Georgia</option>
                      <option value="Verdana">Verdana</option>
                      <option value="Courier New">Courier New</option>
                      <option value="Impact">Impact</option>
                      <option value="Comic Sans MS">Comic Sans MS</option>
                    </select>
                  </div>

                  {/* Description font size */}
                  <div>
                    <label className="text-xs text-gray-500 font-medium mb-1 block">
                      Description font size<span className="text-gray-400 font-normal ml-1">(empty = auto)</span>
                    </label>
                    <NumericTextInput
                      value={descriptionFontSizeOption}
                      min={14}
                      max={40}
                      suffix="px"
                      allowEmpty
                      placeholder="Auto"
                      fallback={14}
                      onChange={(next) => updateDesignOptions({ descriptionFontSize: next })}
                    />
                  </div>
                </div>
              )}
            </div>

            {/* ── DESCRIPTION ─────────────────── */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
              <button
                type="button"
                onClick={() => toggleSection("description")}
                className="w-full flex items-center justify-between px-4 py-3 hover:bg-gray-50 transition-colors"
              >
                <div className="flex items-center gap-2">
                  <Type size={15} className="text-purple-500" />
                  <span className="text-sm font-bold text-gray-900">Description</span>
                </div>
                {openSections.description ? (
                  <ChevronUp size={15} className="text-gray-400" />
                ) : (
                  <ChevronDown size={15} className="text-gray-400" />
                )}
              </button>

              {openSections.description && (
                <div className="px-4 pb-4 border-t border-gray-50 pt-3 space-y-3">
                  {/* Wrap text */}
                  <label className="flex items-center justify-between cursor-pointer">
                    <div>
                      <span className="text-sm font-medium text-gray-800">Wrap text</span>
                      <p className="text-xs text-gray-400">Off = single line + ellipsis</p>
                    </div>
                    <button
                      type="button"
                      role="switch"
                      aria-checked={descriptionWrap}
                      onClick={() => updateDesignOptions({ descriptionWrap: !descriptionWrap })}
                      className={`relative inline-flex items-center w-11 h-6 rounded-full border-2 transition-all focus:outline-none flex-shrink-0 ${descriptionWrap ? "bg-blue-600 border-blue-600" : "bg-white border-gray-300"}`}
                    >
                      <span className={`inline-block w-4 h-4 bg-white rounded-full shadow-md transform transition-transform ${descriptionWrap ? "translate-x-5" : "translate-x-0.5 bg-gray-400"}`} />
                    </button>
                  </label>

                  {/* Width */}
                  <div>
                    <label className="text-xs text-gray-500 font-medium mb-1 block">
                      Width
                    </label>
                    <select
                      value={descriptionWidthPercent}
                      onChange={(e) => updateDesignOptions({ descriptionWidthPercent: Number(e.target.value) })}
                      className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm text-gray-900 outline-none focus:border-blue-500 bg-white"
                    >
                      <option value={50}>Narrow (50%)</option>
                      <option value={75}>Medium (75%)</option>
                      <option value={100}>Full (100%)</option>
                    </select>
                  </div>

                  {/* Max lines */}
                  <div>
                    <label className="text-xs text-gray-500 font-medium mb-1 block">
                      Max lines (1–5)
                    </label>
                    <select
                      value={descriptionMaxLines}
                      disabled={!descriptionWrap}
                      onChange={(e) =>
                        updateDesignOptions({
                          descriptionMaxLines: Number(e.target.value),
                        })
                      }
                      className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm text-gray-900 outline-none focus:border-blue-500 bg-white disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {[1, 2, 3, 4, 5].map((n) => (
                        <option key={n} value={n}>
                          {n} {n === 1 ? "line" : "lines"}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              )}
            </div>

            {/* ── LAYOUT ──────────────────────── */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
              <button
                type="button"
                onClick={() => toggleSection("layout")}
                className="w-full flex items-center justify-between px-4 py-3 hover:bg-gray-50 transition-colors"
              >
                <div className="flex items-center gap-2">
                  <Layout size={15} className="text-blue-600" />
                  <span className="text-sm font-bold text-gray-900">Layout</span>
                </div>
                {openSections.layout ? (
                  <ChevronUp size={15} className="text-gray-400" />
                ) : (
                  <ChevronDown size={15} className="text-gray-400" />
                )}
              </button>

              {openSections.layout && (
                <div className="px-4 pb-4 border-t border-gray-50 pt-3 space-y-3">
                  {/* Top margin — only when bg image */}
                  {sampleImageUrl && (
                    <div>
                      <label className="text-xs text-gray-500 font-medium mb-1 block">
                        Top margin
                      </label>
                      <NumericTextInput
                        value={topMarginPercent}
                        min={0}
                        max={100}
                        onChange={(next) => updateDesignOptions({ topMarginPercent: next })}
                      />
                    </div>
                  )}

                  {/* Price offset */}
                  <div>
                    <label className="text-xs text-gray-500 font-medium mb-1 block">
                      Price horizontal offset
                    </label>
                    <NumericTextInput
                      value={pricePositionPercent}
                      min={0}
                      max={PRICE_OFFSET_MAX_PERCENT}
                      onChange={(next) =>
                        updateDesignOptions({ pricePositionPercent: encodePricePositionPercent(next) })
                      }
                      hint="0% = default, higher = more right"
                    />
                  </div>

                  {/* Item spacing */}
                  <div>
                    <label className="text-xs text-gray-500 font-medium mb-1 block">
                      Item spacing
                    </label>
                    <NumericTextInput
                      value={itemSpacingPercent}
                      min={0}
                      max={100}
                      onChange={(next) => updateDesignOptions({ itemSpacingPercent: next })}
                      hint="0% = tightest, higher = more space"
                    />
                  </div>
                </div>
              )}
            </div>

            {/* ── COLORS ──────────────────────── */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
              <button
                type="button"
                onClick={() => toggleSection("colors")}
                className="w-full flex items-center justify-between px-4 py-3 hover:bg-gray-50 transition-colors"
              >
                <div className="flex items-center gap-2">
                  <Palette size={15} className="text-blue-600" />
                  <span className="text-sm font-bold text-gray-900">Colors</span>
                </div>
                {openSections.colors ? (
                  <ChevronUp size={15} className="text-gray-400" />
                ) : (
                  <ChevronDown size={15} className="text-gray-400" />
                )}
              </button>

              {openSections.colors && (
                <div className="px-4 pb-4 border-t border-gray-50 pt-3 grid grid-cols-2 gap-3">
                  {[
                    { label: "Menu items", key: "menuItemsColor", value: menuItemsColor },
                    { label: "Price", key: "priceColor", value: priceColor },
                    { label: "Item numbers", key: "itemNumberColor", value: itemNumberColor },
                    { label: "Section name", key: "sectionNameColor", value: sectionNameColorOption || "#000000" },
                    { label: "Description", key: "descriptionColor", value: descriptionColorOption || menuItemsColor },
                    { label: "Background", key: "backgroundColor", value: backgroundColor, disabled: !!sampleImageUrl },
                  ].map((c) => (
                    <div key={c.key}>
                      <label className="text-xs text-gray-500 font-medium mb-1 block">
                        {c.label}{c.disabled && <span className="text-gray-300 ml-1">(bg image set)</span>}
                      </label>
                      <div className="flex gap-1.5 items-center">
                        <input
                          type="color"
                          value={c.value}
                          disabled={c.disabled}
                          onChange={(e) => updateDesignOptions({ [c.key]: e.target.value || null })}
                          className="w-8 h-8 p-0.5 rounded-lg border border-gray-200 cursor-pointer disabled:opacity-30 flex-shrink-0"
                        />
                        <input
                          type="text"
                          value={c.value}
                          disabled={c.disabled}
                          onChange={(e) => updateDesignOptions({ [c.key]: e.target.value || null })}
                          className="flex-1 border border-gray-200 rounded-lg px-2 py-1.5 text-xs text-gray-900 outline-none focus:border-blue-500 disabled:opacity-30 min-w-0"
                        />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* ════════════════════════════════════
            RIGHT PANEL — Canvas + Actions
        ════════════════════════════════════ */}
          <div className="flex-1 flex flex-col gap-3 min-w-0">

            {/* Canvas label */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
              <div className="min-w-0">
                <h2 className="text-sm sm:text-base font-bold text-gray-900">
                  {embedMode ? "TV Preview (16:9)" : "TODAY'S MENU"}
                </h2>
                <p className="text-xs text-gray-500 hidden sm:block">Edit on the left — canvas updates live</p>
              </div>
              <span className="text-xs font-semibold bg-blue-50 text-blue-600 border border-blue-200 px-2 py-1 rounded-full self-start sm:self-auto flex-shrink-0">
                16 : 9 Live
              </span>
            </div>

            {/* Section tabs — non-embed only */}
            {!embedMode && sections.length > 0 && (
              <div className="flex gap-2 flex-wrap">
                {sections.slice(0, 5).map((section) => (
                  <button
                    key={section.id}
                    type="button"
                    onClick={() => setActivePreviewSection(section.id)}
                    className={`text-xs font-semibold px-3 py-1.5 rounded-full border transition-colors ${section.id === activePreviewSection ? "bg-blue-600 text-white border-blue-600" : "bg-white text-gray-600 border-gray-200"}`}
                  >
                    {section.name}
                  </button>
                ))}
              </div>
            )}

            {/* Canvas */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
              <div className="relative">
                <canvas
                  ref={liveCanvasRef}
                  className="w-full block"
                  style={{ aspectRatio: "16 / 9" }}
                  width={1920}
                  height={1080}
                  aria-label="Live menu previe"
                />
              </div>
            </div>

            {/* ── NON-EMBED: Show Preview button ─ */}
            {!embedMode && (
              <button
                type="button"
                onClick={() => generateImageWithCanvas()}
                disabled={isGeneratingImage}
                className="w-full bg-blue-600 text-white font-bold text-sm py-3.5 rounded-full flex items-center justify-center gap-2 disabled:opacity-50"
              >
                {isGeneratingImage ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Generating…
                  </>
                ) : (
                  <>
                    <Eye size={15} /> Show Preview
                  </>
                )}
              </button>
            )}

            {/* ── EMBED: Publish + Download ───── */}
            {embedMode && (
              <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4 space-y-3">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                  Publish
                </p>

                {/* Mode select + Publish button */}
                <div className="flex flex-col sm:flex-row gap-2 w-full">
                  <ConfigProvider theme={{ components: { Select: { colorTextPlaceholder: "#595959" } } }}>
                    <Select
                      className="menu-preview-mode-select w-full sm:flex-1"
                      style={{ width: "100%" }}
                      placeholder="Choose a mode"
                      value={selectedPublishModeId || undefined}
                      onChange={(id) => {
                        setSelectedPublishModeId(id);
                        setPublishAction("createNew");
                      }}
                      loading={modesLoading}
                      dropdownRender={(menu) => (
                        <>
                          {menu}
                          <div style={{ padding: "8px 12px", borderTop: "1px solid #f0f0f0" }}>
                            <button
                              type="button"
                              onClick={() => setShowCreateModeModal(true)}
                              className="w-full text-left text-sm text-blue-600 font-medium flex items-center gap-1.5 py-1"
                            >
                              <Plus size={14} /> Create new mode
                            </button>
                          </div>
                        </>
                      )}
                    >
                      {modes.map((m) => (
                        <Select.Option key={m._id} value={m._id}>
                          {m.modeName}
                        </Select.Option>
                      ))}
                    </Select>
                  </ConfigProvider>

                  <button
                    type="button"
                    onClick={async () => {
                      if (!selectedPublishModeId) {
                        message.warning("Select a mode to publish.");
                        return;
                      }
                      if (publishAction === "replace" && !selectedReplacePromoId) {
                        message.warning("Select which menu to replace.");
                        return;
                      }
                      setIsGeneratingImage(true);
                      try {
                        const dataUrl = await generateImageWithCanvas({ skipModal: true });
                        if (dataUrl) await handlePublishToMode(dataUrl);
                      } finally {
                        setIsGeneratingImage(false);
                      }
                    }}
                    disabled={
                      attachLoading ||
                      isGeneratingImage ||
                      !selectedPublishModeId ||
                      (publishAction === "replace" && !selectedReplacePromoId)
                    }
                    className="w-full sm:w-auto bg-blue-600 hover:bg-blue-700 text-white font-bold px-6 py-2.5 rounded-full flex items-center justify-center gap-2 disabled:opacity-50 transition-colors touch-manipulation"
                  >
                    {attachLoading || isGeneratingImage ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        Publishing…
                      </>
                    ) : (
                      <>
                        <Send size={14} /> Publish
                      </>
                    )}
                  </button>
                  {publishedJustNow && (
                    <span className="text-xs font-semibold text-green-600 flex items-center">
                      Published!
                    </span>
                  )}
                </div>

                {selectedPublishModeId && (
                  <div className="mt-3 space-y-2">
                    <Radio.Group
                      value={publishAction}
                      onChange={(e) => setPublishAction(e.target.value)}
                      className="flex flex-col gap-2"
                    >
                      <Radio value="createNew" className="text-sm text-gray-700">
                        Create new menu under &quot;{modes.find((m) => m._id === selectedPublishModeId)?.modeName || "this mode"}&quot;
                      </Radio>
                      <Radio value="replace" className="text-sm text-gray-700">
                        Replace image in this mode
                      </Radio>
                    </Radio.Group>
                    {publishAction === "replace" && (
                      <div className="mt-2 p-3 bg-gray-50 rounded-xl border border-gray-100">
                        <p className="text-xs text-gray-500 mb-2">Select which menu image to replace:</p>
                        {promosInModeLoading ? (
                          <div className="text-xs text-gray-400 py-2">Loading menus…</div>
                        ) : promosInMode.length === 0 ? (
                          <div className="text-xs text-gray-400 py-2">No menus in this mode yet.</div>
                        ) : (
                          <div className="grid grid-cols-3 gap-2">
                            {promosInMode.map((p) => (
                              <button
                                key={p._id}
                                type="button"
                                className={`relative rounded-lg overflow-hidden border p-1 transition-all ${selectedReplacePromoId === p._id
                                  ? "border-blue-500 ring-2 ring-blue-500/20 bg-blue-50/30"
                                  : "border-gray-200 hover:border-gray-300 bg-white"
                                  }`}
                                onClick={() => setSelectedReplacePromoId(selectedReplacePromoId === p._id ? null : p._id)}
                              >
                                <div className="aspect-video w-full rounded bg-gray-100 overflow-hidden relative flex items-center justify-center">
                                  {p.S3link ? (
                                    <img src={p.S3link} alt="" className="object-cover w-full h-full" />
                                  ) : (
                                    <span className="text-[10px] text-gray-400">No image</span>
                                  )}
                                  {selectedReplacePromoId === p._id && (
                                    <div className="absolute inset-0 bg-blue-600/10 flex items-center justify-center">
                                      <span className="w-5 h-5 bg-blue-600 text-white rounded-full flex items-center justify-center text-xs font-bold shadow">
                                        ✓
                                      </span>
                                    </div>
                                  )}
                                </div>
                                <div className="text-[10px] font-medium text-gray-700 truncate mt-1 text-center px-1">
                                  {p.title || "Untitled"}
                                </div>
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}

                <button
                  type="button"
                  onClick={async () => {
                    setIsGeneratingImage(true);
                    try {
                      const dataUrl = await generateImageWithCanvas({ skipModal: true });
                      if (dataUrl) {
                        const link = document.createElement("a");
                        link.href = dataUrl;
                        link.download = `menu-${activeSelectionLabel || "menu"}-${Date.now()}.png`;
                        link.click();
                        message.success("Image downloaded!");
                      }
                    } finally {
                      setIsGeneratingImage(false);
                    }
                  }}
                  disabled={isGeneratingImage}
                  className="w-full flex items-center justify-center gap-2 border border-gray-200 hover:border-gray-300 text-gray-700 font-medium py-2.5 rounded-full text-sm transition-colors mt-2"
                >
                  {isGeneratingImage ? (
                    <>
                      <div className="w-4 h-4 border-2 border-gray-500 border-t-transparent rounded-full animate-spin" />
                      Generating…
                    </>
                  ) : (
                    <>
                      <Download size={14} /> Download image
                    </>
                  )}
                </button>
              </div>
            )}
          </div>

        </div>
      </div>

      {/* Preview Modal */}
      <Modal
        title="Menu Preview"
        open={showPreviewModal}
        onCancel={() => {
          setShowPreviewModal(false);
          setPreviewImageUrl(null);
          setSelectedPublishModeId(null);
          setPublishAction("createNew");
          setSelectedReplacePromoId(null);
        }}
        footer={null}
        width={920}
        centered
      >
        {previewImageUrl && (
          <>
            <div className="max-h-[55vh] overflow-auto flex justify-center p-4 rounded-xl bg-gray-50 border border-gray-100">
              <img src={previewImageUrl} alt="Menu Preview" className="max-w-full h-auto block rounded-lg shadow-sm" />
            </div>

            <div className="mt-4 p-4 bg-gray-50 border border-gray-100 rounded-2xl">
              <p className="text-sm font-bold text-gray-900 mb-3">Publish to Promo Ads</p>
              <div className="flex flex-col sm:flex-row gap-2 w-full">
                <ConfigProvider theme={{ components: { Select: { colorTextPlaceholder: "#595959" } } }}>
                  <Select
                    className="menu-preview-mode-select w-full sm:flex-1"
                    placeholder="Choose a mode"
                    value={selectedPublishModeId || undefined}
                    onChange={(id) => {
                      setSelectedPublishModeId(id);
                      setPublishAction("createNew");
                    }}
                    loading={modesLoading}
                    style={{ width: "100%" }}
                    dropdownRender={(menu) => (
                      <>
                        {menu}
                        <div style={{ padding: "8px 12px", borderTop: "1px solid #f0f0f0" }}>
                          <button
                            type="button"
                            onClick={() => setShowCreateModeModal(true)}
                            className="w-full text-left text-sm text-blue-600 font-medium flex items-center gap-1.5 py-1"
                          >
                            <Plus size={14} /> Create new mode
                          </button>
                        </div>
                      </>
                    )}
                  >
                    {modes.map((m) => (
                      <Select.Option key={m._id} value={m._id}>
                        {m.modeName}
                      </Select.Option>
                    ))}
                  </Select>
                </ConfigProvider>
                <button
                  type="button"
                  onClick={() => handlePublishToMode()}
                  disabled={
                    attachLoading ||
                    !selectedPublishModeId ||
                    (publishAction === "replace" && !selectedReplacePromoId)
                  }
                  className="bg-blue-600 hover:bg-blue-700 text-white font-bold px-5 py-2 rounded-full text-sm transition-colors flex items-center gap-1.5 disabled:opacity-50"
                >
                  {attachLoading ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Publishing…
                    </>
                  ) : (
                    <>
                      <Send size={14} /> Publish
                    </>
                  )}
                </button>
                {publishedJustNow && (
                  <span className="text-xs font-semibold text-green-600 flex items-center">Published!</span>
                )}
              </div>

              {selectedPublishModeId && (
                <div className="mt-3 space-y-2">
                  <Radio.Group
                    value={publishAction}
                    onChange={(e) => setPublishAction(e.target.value)}
                    className="flex flex-col gap-2"
                  >
                    <Radio value="createNew" className="text-sm text-gray-700">
                      Create new (sub) mode under &quot;{modes.find((m) => m._id === selectedPublishModeId)?.modeName || "this mode"}&quot;
                    </Radio>
                    <Radio value="replace" className="text-sm text-gray-700">
                      Replace image in this mode
                    </Radio>
                  </Radio.Group>
                  {publishAction === "replace" && (
                    <div className="mt-2 p-3 bg-gray-50 rounded-xl border border-gray-100">
                      <p className="text-xs text-gray-500 mb-2">Select which menu image to replace:</p>
                      {promosInModeLoading ? (
                        <div className="text-xs text-gray-400 py-2">Loading menus…</div>
                      ) : promosInMode.length === 0 ? (
                        <div className="text-xs text-gray-400 py-2">No menus in this mode yet.</div>
                      ) : (
                        <div className="grid grid-cols-3 gap-2">
                          {promosInMode.map((p) => (
                            <button
                              key={p._id}
                              type="button"
                              className={`relative rounded-lg overflow-hidden border p-1 transition-all ${selectedReplacePromoId === p._id
                                ? "border-blue-500 ring-2 ring-blue-500/20 bg-blue-50/30"
                                : "border-gray-200 hover:border-gray-300 bg-white"
                                }`}
                              onClick={() => setSelectedReplacePromoId(selectedReplacePromoId === p._id ? null : p._id)}
                            >
                              <div className="aspect-video w-full rounded bg-gray-100 overflow-hidden relative flex items-center justify-center">
                                {p.S3link ? (
                                  <img src={p.S3link} alt="" className="object-cover w-full h-full" />
                                ) : (
                                  <span className="text-[10px] text-gray-400">No image</span>
                                )}
                                {selectedReplacePromoId === p._id && (
                                  <div className="absolute inset-0 bg-blue-600/10 flex items-center justify-center">
                                    <span className="w-5 h-5 bg-blue-600 text-white rounded-full flex items-center justify-center text-xs font-bold shadow">
                                      ✓
                                    </span>
                                  </div>
                                )}
                              </div>
                              <div className="text-[10px] font-medium text-gray-700 truncate mt-1 text-center px-1">
                                {p.title || "Untitled"}
                              </div>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Footer actions */}
            <div className="mt-5 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => {
                  setShowPreviewModal(false);
                  setPreviewImageUrl(null);
                  setSelectedPublishModeId(null);
                  setPublishAction("createNew");
                  setSelectedReplacePromoId(null);
                }}
                className="border border-gray-200 hover:bg-gray-50 text-gray-700 font-medium px-5 py-2 rounded-full text-sm transition-colors"
              >
                Close
              </button>
              <button
                type="button"
                onClick={() => {
                  if (!previewImageUrl) return;
                  const link = document.createElement("a");
                  link.href = previewImageUrl;
                  link.download = `menu-${activeSelectionLabel || "menu"}-${Date.now()}.png`;
                  link.click();
                  message.success("Image downloaded!");
                }}
                className="bg-blue-600 hover:bg-blue-700 text-white font-bold px-5 py-2 rounded-full text-sm transition-colors flex items-center gap-1.5"
              >
                <Download size={14} /> Download
              </button>
            </div>
          </>
        )}
      </Modal>

      {/* Create New Mode Modal */}
      <Modal
        title="Create New Mode"
        open={showCreateModeModal}
        onCancel={() => {
          setShowCreateModeModal(false);
          setNewModeName("");
        }}
        onOk={handleCreateMode}
        confirmLoading={createModeLoading}
        okText="Create Mode"
      >
        <div className="pt-2">
          <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Mode Name</label>
          <Input
            placeholder="e.g. Breakfast, Lunch, Happy Hour"
            value={newModeName}
            onChange={(e) => setNewModeName(e.target.value)}
            onPressEnter={handleCreateMode}
            size="large"
            style={{ borderRadius: 8 }}
          />
          <p className="text-xs text-gray-500 mt-2">
            Modes organize your promo ads. Create one for each display scenario.
          </p>
        </div>
      </Modal>
    </>
  );
};

export default MenuPreviewPanel;
